import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';

declare global {
    interface Window {
        appendHelcimPayIframe?: (checkoutToken: string) => void;
    }
}

import {
    Container,
    Box,
    Typography,
    Button,
    Alert,
    CircularProgress,
    Breadcrumbs,
    Chip,
    Grid,
    Card,
    CardContent,
    CardHeader,
    List,
    ListItem,
    ListItemText,
    ListItemButton,
    IconButton,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    TextField,
    FormControl,
    FormControlLabel,
    InputLabel,
    Select,
    MenuItem,
    Switch,
    Tabs,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Pagination,
    TablePagination,
    Slider,
    Collapse,
    Menu,
    ListItemIcon,
    InputAdornment
} from '@mui/material';
import {
    ArrowBack as ArrowBackIcon,
    NavigateNext as NavigateNextIcon,
    Home as HomeIcon,
    Edit as EditIcon,
    Person as PersonIcon,
    CardMembership as CardMembershipIcon,
    History as HistoryIcon,
    Info as InfoIcon,
    Add as AddIcon,
    Delete as DeleteIcon,
    Payment as PaymentIcon,
    PhotoCamera as PhotoCameraIcon,
    ZoomIn as ZoomInIcon,
    ZoomOut as ZoomOutIcon,
    CreditCard as CreditCardIcon,
    FitnessCenter as FitnessCenterIcon,
    Assignment as AssignmentIcon,
    CheckCircle as CheckCircleIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Download as DownloadIcon,
    ExpandMore as ExpandMoreIcon,
    CameraAlt as CameraAltIcon,
    Upload as UploadIcon,
    Close as CloseIcon,
    Search as SearchIcon,
    Clear as ClearIcon
} from '@mui/icons-material';
import ReactCrop, { Crop, PixelCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Link as RouterLink } from 'react-router-dom';
import { DataGrid, GridColDef, GridRenderCellParams } from '@mui/x-data-grid';
import { memberProfileAPI, memberGroupAPI, membershipTierAPI, auditLogAPI, oneTimeFeeAPI, discountAPI, transactionAPI, paymentMethodAPI, documentReviewAPI, addOnAPI, tenantSettingsAPI } from '../../services/api';
import { MemberProfile, MembershipStatus, GroupRole } from '../../types/memberProfile';
import PTPackManager from '../personalTraining/PTPackManager';
import { MemberGroup, MembershipTier, AuditLog, OneTimeFee, Discount, Transaction, TenantSettings } from '../../types';
import { generateInvoiceHTML, openInvoiceWindow } from '../../utils/invoiceGenerator';
import { MembershipAddOn, MemberAddOn, SubscriptionPreview } from '../../types/addOn';
import { formatDate } from '../../utils/dateUtils';
import { useAuth } from '../../context/AuthContext';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`tabpanel-${index}`}
            aria-labelledby={`tab-${index}`}
            {...other}
        >
            {value === index && (
                <Box sx={{ pt: 3 }}>
                    {children}
                </Box>
            )}
        </div>
    );
}

function a11yProps(index: number) {
    return {
        id: `tab-${index}`,
        'aria-controls': `tabpanel-${index}`,
    };
}

const MemberProfileDetailsPage: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { user: currentUser } = useAuth();
    const [memberProfile, setMemberProfile] = useState<MemberProfile | null>(null);
    const [memberGroup, setMemberGroup] = useState<MemberGroup | null>(null);
    const [membershipTiers, setMembershipTiers] = useState<MembershipTier[]>([]);
    const [pendingScheduledChange, setPendingScheduledChange] = useState<any>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [editDialogOpen, setEditDialogOpen] = useState<boolean>(false);
    const [currentTab, setCurrentTab] = useState<number>(0);
    const [auditLogs, setAuditLogs] = useState<AuditLog[]>([]);
    const [auditLogsLoading, setAuditLogsLoading] = useState<boolean>(false);
    const [auditLogsError, setAuditLogsError] = useState<string | null>(null);
    const [auditLogsPagination, setAuditLogsPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    });

    // Transactions state
    const [transactions, setTransactions] = useState<Transaction[]>([]);
    const [transactionsLoading, setTransactionsLoading] = useState<boolean>(false);
    const [transactionsError, setTransactionsError] = useState<string | null>(null);
    const [transactionsPagination, setTransactionsPagination] = useState({
        page: 1,
        limit: 10,
        total: 0,
        pages: 0
    });
    const [selectedTransaction, setSelectedTransaction] = useState<Transaction | null>(null);
    const [transactionDetailsOpen, setTransactionDetailsOpen] = useState(false);
    const [groupMembers, setGroupMembers] = useState<any[]>([]);
    const [primaryContact, setPrimaryContact] = useState<any>(null);
    const [groupMessageExpanded, setGroupMessageExpanded] = useState(false);
    const [adminNote, setAdminNote] = useState<string>('');
    const [cancelConfirmOpen, setCancelConfirmOpen] = useState<boolean>(false);
    const [tenantSettings, setTenantSettings] = useState<TenantSettings | null>(null);
    
    // Cancellation confirmation dialog state
    const [cancellationConfirmOpen, setCancellationConfirmOpen] = useState(false);
    const [pendingStatusChange, setPendingStatusChange] = useState<string | null>(null);
    const [cancellationType, setCancellationType] = useState<'immediate' | 'scheduled' | null>(null);
    const [checkingCancellation, setCheckingCancellation] = useState(false);
    const [refundConfirmOpen, setRefundConfirmOpen] = useState<boolean>(false);
    const [processingAction, setProcessingAction] = useState<boolean>(false);
    const [retryPaymentDialogOpen, setRetryPaymentDialogOpen] = useState<boolean>(false);
    const [retryPaymentProcessing, setRetryPaymentProcessing] = useState<boolean>(false);
    const [retryPaymentResults, setRetryPaymentResults] = useState<Array<{
        transactionId: string;
        success: boolean;
        message: string;
        type?: string;
        amount?: number;
    }>>([]);
    const [retryFailedTransactionsCount, setRetryFailedTransactionsCount] = useState<number>(0);

    // Payment methods state
    const [paymentMethods, setPaymentMethods] = useState<any[]>([]);
    const [paymentMethodsLoading, setPaymentMethodsLoading] = useState<boolean>(false);
    const [paymentMethodsError, setPaymentMethodsError] = useState<string | null>(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [helcimScriptLoaded, setHelcimScriptLoaded] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [cardToDelete, setCardToDelete] = useState<string | null>(null);
    const secretTokenRef = React.useRef<string>('');
    const checkoutTokenRef = React.useRef<string>('');
    const helcimObserverRef = React.useRef<MutationObserver | null>(null);
    const helcimIntervalRef = React.useRef<number | null>(null);
    const helcimIframeRef = React.useRef<HTMLIFrameElement | null>(null);
    const memberProfileRef = React.useRef<any>(null);

    // Onboarding state
    const [onboardingStatus, setOnboardingStatus] = useState<any>(null);
    const [onboardingLoading, setOnboardingLoading] = useState<boolean>(false);
    const [onboardingError, setOnboardingError] = useState<string | null>(null);

    // Fees and discounts state
    const [oneTimeFees, setOneTimeFees] = useState<OneTimeFee[]>([]);
    const [selectedFees, setSelectedFees] = useState<OneTimeFee[]>([]);
    const [originalSelectedFees, setOriginalSelectedFees] = useState<OneTimeFee[]>([]); // Track original fees to detect changes
    const [discounts, setDiscounts] = useState<Discount[]>([]);
    const [selectedDiscounts, setSelectedDiscounts] = useState<Discount[]>([]);
    const [feeDialogOpen, setFeeDialogOpen] = useState(false);
    const [discountDialogOpen, setDiscountDialogOpen] = useState(false);

    // Scheduled downgrade dialog state
    const [scheduledDowngradeDialogOpen, setScheduledDowngradeDialogOpen] = useState(false);
    const [scheduledDowngradeMessage, setScheduledDowngradeMessage] = useState<string>('');

    // View/Cancel scheduled change dialog state
    const [scheduledChangeDialogOpen, setScheduledChangeDialogOpen] = useState(false);
    const [scheduledChangeDetails, setScheduledChangeDetails] = useState<any>(null);
    const [scheduledChangeLoading, setScheduledChangeLoading] = useState(false);
    const [cancelingScheduledChange, setCancelingScheduledChange] = useState(false);

    // Error dialog state
    const [errorDialogOpen, setErrorDialogOpen] = useState(false);
    const [errorDialogMessage, setErrorDialogMessage] = useState('');

    // Downgrade confirmation dialog state
    const [downgradeConfirmOpen, setDowngradeConfirmOpen] = useState(false);
    const [downgradeTierInfo, setDowngradeTierInfo] = useState<{
        currentTierName: string;
        newTierName: string;
        currentPrice: number;
        newPrice: number;
        effectiveDate: string;
    } | null>(null);

    // Upgrade confirmation dialog state
    const [upgradeConfirmOpen, setUpgradeConfirmOpen] = useState(false);
    const [upgradeTierInfo, setUpgradeTierInfo] = useState<{
        currentTierName: string;
        newTierName: string;
        currentPrice: number;
        newPrice: number;
        proratedCharge: number;
    } | null>(null);

    // Discount application dialog state
    const [discountApplicationDialogOpen, setDiscountApplicationDialogOpen] = useState(false);
    const [pendingDiscountAction, setPendingDiscountAction] = useState<{
        action: 'add' | 'remove';
        discount: Discount;
    } | null>(null);
    const [pendingMembershipTransactions, setPendingMembershipTransactions] = useState<Transaction[]>([]);
    const [discountApplicationLoading, setDiscountApplicationLoading] = useState(false);

    // Add-ons state
    const [availableAddOns, setAvailableAddOns] = useState<MembershipAddOn[]>([]);
    const [memberAddOns, setMemberAddOns] = useState<MemberAddOn[]>([]);
    const [addOnDialogOpen, setAddOnDialogOpen] = useState(false);
    const [addOnLoading, setAddOnLoading] = useState(false);
    const [previewDialogOpen, setPreviewDialogOpen] = useState(false);
    const [previewData, setPreviewData] = useState<SubscriptionPreview | null>(null);
    const [selectedAddOnId, setSelectedAddOnId] = useState<string | null>(null);

    // Profile picture state
    const [profilePictureHovered, setProfilePictureHovered] = useState(false);
    const [uploadingPicture, setUploadingPicture] = useState(false);
    const [profilePictureBlobUrl, setProfilePictureBlobUrl] = useState<string | null>(null);
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Crop dialog state
    const [cropDialogOpen, setCropDialogOpen] = useState(false);
    const [imageSrc, setImageSrc] = useState<string | null>(null);
    const [crop, setCrop] = useState<Crop>({
        unit: '%',
        width: 90,
        height: 90,
        x: 5,
        y: 5
    });
    const [completedCrop, setCompletedCrop] = useState<PixelCrop | null>(null);
    const [zoom, setZoom] = useState<number>(1);
    const imgRef = useRef<HTMLImageElement | null>(null);
    const [originalFile, setOriginalFile] = useState<File | null>(null);

    // Camera dialog state
    const [uploadOptionsOpen, setUploadOptionsOpen] = useState(false);
    const [cameraDialogOpen, setCameraDialogOpen] = useState(false);
    const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
    const videoRef = useRef<HTMLVideoElement | null>(null);
    const canvasRef = useRef<HTMLCanvasElement | null>(null);

    // Edit form state
    const [editForm, setEditForm] = useState({
        firstName: '',
        lastName: '',
        email: '',
        phone: '',
        mobile: '',
        address: '',
        city: '',
        state: '',
        zipCode: '',
        country: '',
        dateOfBirth: '',
        membershipTierId: '',
        startDate: '',
        status: '',
        monthlyPrice: '',
        autoRenew: false,
        membershipNumber: '',
        notes: '',
        emailUpdates: true
    });

    useEffect(() => {
        if (id) {
            fetchMemberProfile();
            fetchMembershipTiers();
            fetchOneTimeFees();
            fetchDiscounts();
            fetchAvailableAddOns();
            fetchMemberAddOns();
            fetchTenantSettings();
        }
    }, [id]);

    useEffect(() => {
        if (id && currentTab === 1) {
            fetchAuditLogs();
        }
    }, [id, currentTab, auditLogsPagination.page]);

    useEffect(() => {
        if (id && currentTab === 2) {
            fetchTransactions();
        }
    }, [id, currentTab]);

    useEffect(() => {
        if (id && currentTab === 3) {
            fetchPaymentMethods();
        }
    }, [id, currentTab]);

    useEffect(() => {
        if (id && currentTab === 4) {
            fetchOnboardingStatus();
        }
    }, [id, currentTab]);

    // Fetch group members and primary contact when memberGroup is loaded
    useEffect(() => {
        if (memberGroup?.id) {
            fetchGroupMembers(memberGroup.id);
            
            // If there's a primary contact ID, fetch their details
            if (memberGroup.primaryContactId) {
                fetchPrimaryContact(memberGroup.primaryContactId);
            }
        } else {
            setGroupMembers([]);
            setPrimaryContact(null);
        }
    }, [memberGroup?.id, memberGroup?.primaryContactId]);

    // Load Helcim script
    useEffect(() => {
        const script = document.createElement('script');
        script.src = 'https://secure.helcim.app/helcim-pay/services/start.js';
        script.async = true;
        script.onload = () => {
            setHelcimScriptLoaded(true);
            if ((window as any).HelcimPay) {
                try {
                    (window as any).HelcimPay.init({});
                } catch (err) {
                    console.warn('HelcimPay init failed or not required', err);
                }
            }
        };
        script.onerror = () => {
            console.error('Failed to load HelcimPay script');
        };
        document.body.appendChild(script);

        return () => {
            if (document.body.contains(script)) document.body.removeChild(script);
            setHelcimScriptLoaded(false);
        };
    }, []);

    // Cleanup Helcim watcher on unmount
    useEffect(() => {
        return () => {
            disconnectWatcher();
        };
    }, []);

    // Keep memberProfileRef in sync with memberProfile state
    useEffect(() => {
        memberProfileRef.current = memberProfile;
    }, [memberProfile]);

    // Listen for Helcim postMessage events
    useEffect(() => {
        const handler = async (event: MessageEvent) => {
            try {
                if (!event?.data) return;

                const token = checkoutTokenRef.current;
                if (!token) return;

                const helcimPayJsIdentifierKey = `helcim-pay-js-${token}`;

                if (event.data.eventName !== helcimPayJsIdentifierKey) return;

                const status = event.data.eventStatus;
                if (status === 'ABORTED') {
                    console.error('Transaction failed!', event.data.eventMessage);
                    setError(event.data.eventMessage || 'Payment was aborted');
                    setIsSubmitting(false);
                }

                if (status === 'SUCCESS') {
                    await handleVerificationSuccess(event.data);
                    const win = window as any;
                    if (win.HelcimPay && typeof win.HelcimPay.close === 'function') {
                        try { win.HelcimPay.close(); } catch (err) { /* ignore */ }
                    }
                    // Don't call setIsSubmitting(false) here - it's handled in handleVerificationSuccess's finally block
                }
            } catch (err) {
                console.error('Error handling Helcim message event', err);
                setIsSubmitting(false); // Ensure we reset on any error
            }
        };

        window.addEventListener('message', handler);
        return () => window.removeEventListener('message', handler);
    }, []); // Empty dependency array - use refs for accessing current state

    // Fetch profile picture as blob
    useEffect(() => {
        const fetchProfilePicture = async () => {
            if (memberProfile?.profilePictureUrl && id) {
                try {
                    const response = await memberProfileAPI.getProfilePicture(id);
                    const blob = new Blob([response.data], { type: response.headers['content-type'] || 'image/jpeg' });
                    const blobUrl = URL.createObjectURL(blob);
                    setProfilePictureBlobUrl(blobUrl);
                } catch (err) {
                    console.error('Error fetching profile picture:', err);
                    // If fetch fails, keep null and show placeholder
                }
            } else {
                // Clean up blob URL if no picture
                if (profilePictureBlobUrl) {
                    URL.revokeObjectURL(profilePictureBlobUrl);
                    setProfilePictureBlobUrl(null);
                }
            }
        };

        fetchProfilePicture();

        // Cleanup function to revoke blob URL
        return () => {
            if (profilePictureBlobUrl) {
                URL.revokeObjectURL(profilePictureBlobUrl);
            }
        };
    }, [memberProfile?.profilePictureUrl, id]);

    // Cleanup camera stream on unmount
    useEffect(() => {
        return () => {
            if (cameraStream) {
                cameraStream.getTracks().forEach(track => track.stop());
            }
        };
    }, [cameraStream]);

    const fetchAuditLogs = async () => {
        if (!id) return;

        setAuditLogsLoading(true);
        setAuditLogsError(null);

        try {
            const response = await auditLogAPI.getEntityAuditLogs(
                'MemberProfile',
                id,
                {
                    page: auditLogsPagination.page,
                    limit: auditLogsPagination.limit
                }
            );

            setAuditLogs(response.data.data || []);
            setAuditLogsPagination(prev => ({
                ...prev,
                total: response.data.pagination?.total || 0,
                pages: response.data.pagination?.pages || 0
            }));
        } catch (err) {
            console.error('Error fetching audit logs:', err);
            setAuditLogsError('Failed to load audit logs. You may not have permission to view this information.');
        } finally {
            setAuditLogsLoading(false);
        }
    };

    const fetchTransactions = async () => {
        if (!id) return;

        setTransactionsLoading(true);
        setTransactionsError(null);

        try {
            // Fetch all transactions - DataGrid will handle pagination and filtering
            const response = await transactionAPI.getMemberProfileTransactions(
                id,
                {
                    limit: 1000, // Get a large number of transactions
                    offset: 0
                }
            );

            // Handle different possible response structures
            let transactionData = [];
            let totalCount = 0;

            if (response.data) {
                if (Array.isArray(response.data)) {
                    transactionData = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    transactionData = response.data.data;
                } else if (response.data.transactions && Array.isArray(response.data.transactions)) {
                    transactionData = response.data.transactions;
                    // Backend returns total directly in response.data.total
                    totalCount = response.data.total || 0;
                }
            }

            // Sort transactions by createdAt timestamp (latest first)
            transactionData.sort((a: { createdAt: string }, b: { createdAt: string }) => {
                const dateA = new Date(a.createdAt).getTime();
                const dateB = new Date(b.createdAt).getTime();
                return dateB - dateA; // Descending order (latest first)
            });

            setTransactions(transactionData);

            // Count failed transactions for retry button
            const failedCount = transactionData.filter(
                (tx: Transaction) => tx.status === 'failed'
            ).length;
            setRetryFailedTransactionsCount(failedCount);

            // Update pagination info with correct total count
            setTransactionsPagination(prev => ({
                ...prev,
                total: totalCount,
                pages: Math.ceil(totalCount / prev.limit)
            }));
        } catch (err) {
            console.error('Error fetching transactions:', err);
            setTransactionsError('Failed to load transactions. You may not have permission to view this information.');
            setTransactions([]); // Ensure transactions is always an array
        } finally {
            setTransactionsLoading(false);
        }
    };

    const fetchPaymentMethods = async () => {
        if (!id) return;

        setPaymentMethodsLoading(true);
        setPaymentMethodsError(null);

        try {
            // Get payment methods for the specific member profile (admin view)
            const response = await paymentMethodAPI.getMemberPaymentMethods(id);
            setPaymentMethods(response.data.paymentMethods || []);
        } catch (err) {
            console.error('Error fetching payment methods:', err);
            setPaymentMethodsError('Failed to load payment methods.');
            setPaymentMethods([]);
        } finally {
            setPaymentMethodsLoading(false);
        }
    };

    const fetchTenantSettings = async () => {
        try {
            const response = await tenantSettingsAPI.getAllSettings();
            setTenantSettings(response.data);
        } catch (err) {
            console.error('Error fetching tenant settings:', err);
        }
    };

    const fetchOnboardingStatus = async () => {
        if (!id) return;

        setOnboardingLoading(true);
        setOnboardingError(null);

        try {
            const response = await documentReviewAPI.getReviewStatus(id);
            setOnboardingStatus(response.data);
        } catch (err) {
            console.error('Error fetching onboarding status:', err);
            setOnboardingError('Failed to load onboarding status.');
            setOnboardingStatus(null);
        } finally {
            setOnboardingLoading(false);
        }
    };

    const fetchMembershipTiers = async () => {
        try {
            const response = await membershipTierAPI.getMembershipTiers(true);
            setMembershipTiers(response.data);
        } catch (err) {
            console.error('Error fetching membership tiers:', err);
            // Don't set error for tiers fetch failure, just log it
        }
    };

    const isCardExpired = (expiryMonth: number, expiryYear: number) => {
        if (!expiryMonth || !expiryYear) return false;
        const now = new Date();
        const currentYear = now.getFullYear();
        const currentMonth = now.getMonth() + 1;

        if (expiryYear < currentYear) return true;
        if (expiryYear === currentYear && expiryMonth < currentMonth) return true;
        return false;
    };

    const handleDeleteCard = async (cardId: string) => {
        if (!id) return;

        try {
            // Pass the member profile ID for admin operations
            await paymentMethodAPI.deletePaymentMethod(cardId, id);
            setDeleteDialogOpen(false);
            setCardToDelete(null);
            await fetchPaymentMethods();
            setSuccess('Payment method deleted successfully!');
            setTimeout(() => setSuccess(null), 5000);
        } catch (error: any) {
            console.error('Error deleting payment method:', error);
            setError(error?.response?.data?.message || 'Failed to delete payment method');
            setDeleteDialogOpen(false);
        }
    };

    const handleSetDefaultCard = async (cardId: string) => {
        if (!id) return;

        try {
            // Pass the member profile ID for admin operations
            await paymentMethodAPI.setDefaultPaymentMethod(cardId, id);
            await fetchPaymentMethods();
            setSuccess('Default payment method updated successfully!');
            setTimeout(() => setSuccess(null), 5000);
        } catch (error: any) {
            console.error('Error setting default payment method:', error);
            setError(error?.response?.data?.message || 'Failed to set default payment method');
        }
    };

    const openDeleteDialog = (cardId: string) => {
        setCardToDelete(cardId);
        setDeleteDialogOpen(true);
    };

    const handleUpdateCardOnFile = () => {
        initializeHelcimPay();
    };

    const initializeHelcimPay = async () => {
        if (!memberProfile) return;

        try {
            const response = await paymentMethodAPI.initializePaymentProcessor();

            if (response?.data) {
                secretTokenRef.current = response.data.secretToken || '';
                checkoutTokenRef.current = response.data.checkoutToken || '';
            }

            setIsSubmitting(true);

            const helcimPayConfig = {
                token: secretTokenRef.current || '',
                amount: 0,
                currency: 'CAD',
                transactionType: 'verify' as const,
                customer: {
                    contactName: `${memberProfile?.firstName} ${memberProfile?.lastName}`,
                    emailAddress: memberProfile?.email,
                    phoneNumber: memberProfile?.phone,
                    cellPhoneNumber: memberProfile?.mobile
                }
            };

            const win = window as any;

            if (typeof win.appendHelcimPayIframe === 'function' && checkoutTokenRef.current) {
                try {
                    win.appendHelcimPayIframe(checkoutTokenRef.current);
                    startHelcimCloseWatcher();
                    return;
                } catch (err) {
                    console.error('appendHelcimPayIframe failed', err);
                }
            }

            if (win.HelcimPay && typeof win.HelcimPay.render === 'function') {
                win.HelcimPay.render(helcimPayConfig);
                if (typeof win.HelcimPay.open === 'function') {
                    win.HelcimPay.open();
                }
                startHelcimCloseWatcher();
                return;
            }

            console.error('HelcimPay is not initialized');
            setError('Payment system is not available. Please refresh the page and try again.');
            setIsSubmitting(false);
        } catch (error) {
            console.error('Error initializing HelcimPay:', error);
            setError('Failed to initialize payment system. Please try again.');
            setIsSubmitting(false);
        }
    };

    const startHelcimCloseWatcher = () => {
        if (helcimObserverRef.current) return;

        const checkForIframe = () => {
            const iframes = Array.from(document.getElementsByTagName('iframe')) as HTMLIFrameElement[];
            for (const f of iframes) {
                const src = f.src || '';
                if (src.toLowerCase().includes('helcim') || src.toLowerCase().includes('helcim-pay')) {
                    helcimIframeRef.current = f;
                    return f;
                }
            }
            return null;
        };

        const observer = new MutationObserver((mutations) => {
            if (!helcimIframeRef.current) {
                const found = checkForIframe();
                if (found) {
                    return;
                }
            } else {
                if (!document.body.contains(helcimIframeRef.current)) {
                    setIsSubmitting(false);
                    disconnectWatcher();
                }
            }
        });

        helcimObserverRef.current = observer;
        observer.observe(document.body, { childList: true, subtree: true });

        const existing = checkForIframe();
        if (existing) {
            helcimIframeRef.current = existing;
        }

        helcimIntervalRef.current = window.setInterval(() => {
            const f = helcimIframeRef.current;
            if (f) {
                if (!document.body.contains(f)) {
                    setIsSubmitting(false);
                    disconnectWatcher();
                    return;
                }
                try {
                    if (f.offsetParent === null) {
                        setIsSubmitting(false);
                        disconnectWatcher();
                        return;
                    }
                } catch (err) {
                    // ignore cross-origin access issues
                }
            }

            const helcimNodes = Array.from(document.querySelectorAll('[id*="helcim" i], [class*="helcim" i]'));
            if (helcimNodes.length === 0 && helcimIframeRef.current) {
                setIsSubmitting(false);
                disconnectWatcher();
            }
        }, 500);
    };

    const disconnectWatcher = (removeElements = false) => {
        if (helcimObserverRef.current) {
            helcimObserverRef.current.disconnect();
            helcimObserverRef.current = null;
        }
        if (helcimIntervalRef.current) {
            clearInterval(helcimIntervalRef.current);
            helcimIntervalRef.current = null;
        }

        // Only remove DOM elements if explicitly requested (after successful payment)
        if (removeElements) {
            // Remove all Helcim iframes and containers from the DOM
            const helcimIframes = document.querySelectorAll('iframe[src*="helcim"], iframe[src*="helcimpay"]');
            helcimIframes.forEach(iframe => iframe.remove());

            // Remove any Helcim containers
            const helcimContainers = document.querySelectorAll('[id*="helcim" i], [class*="helcim" i]');
            helcimContainers.forEach(container => {
                // Only remove elements that look like Helcim overlays/modals
                if (container.tagName !== 'BUTTON' && !container.closest('button')) {
                    container.remove();
                }
            });
        }

        helcimIframeRef.current = null;
    };

    const handleVerificationSuccess = async (result: any) => {
        const currentMemberProfile = memberProfileRef.current;

        if (!currentMemberProfile?.id) {
            return;
        }

        try {
            let transactionData = null;
            if (result.eventMessage && typeof result.eventMessage === 'string') {
                try {
                    const parsed = JSON.parse(result.eventMessage);
                    transactionData = parsed.data?.data || parsed.data || parsed;
                } catch (e) {
                    console.error('Failed to parse eventMessage:', e);
                }
            }

            if (!transactionData) {
                console.error('No transaction data found in result:', result);
                throw new Error('Invalid payment response received. Please try again.');
            }

            const cardToken = transactionData.cardToken;
            const customerId = transactionData.customerCode;
            const cardNumber = {
                lastFour: transactionData.cardNumber?.slice(-4) || '****',
                brand: transactionData.cardType || 'Unknown',
                cardHolderName: transactionData.cardHolderName
            };

            if (!cardToken) {
                console.error('No card token found in transaction data:', transactionData);
                throw new Error('Card token not found in payment response. Please try again.');
            }

            await paymentMethodAPI.addPaymentMethodToken({
                cardToken,
                customerId,
                cardNumber,
                verificationMessage: result.eventMessage || JSON.stringify(result),
                memberProfileId: currentMemberProfile.id
            });

            if (typeof (window as any).HelcimPay !== 'undefined' && (window as any).HelcimPay.close) {
                (window as any).HelcimPay.close();
            }

            // Disconnect the watcher and remove Helcim elements from DOM
            disconnectWatcher(true);

            // Add a small delay to ensure database transaction is committed
            await new Promise(resolve => setTimeout(resolve, 500));

            await fetchPaymentMethods();
            setSuccess('Payment method updated successfully!');
            setError(null);

            setTimeout(() => {
                setSuccess(null);
            }, 5000);
        } catch (error: any) {
            console.error('Error storing payment method:', error);
            const errorMessage = error?.response?.data?.message || error?.message || 'Failed to store payment method.';
            setError(errorMessage);
            // Also disconnect watcher and remove elements on error
            disconnectWatcher(true);
        } finally {
            setIsSubmitting(false);
        }
    };

    // Fetch one-time fees
    const fetchOneTimeFees = async () => {
        try {
            const response = await oneTimeFeeAPI.getAll();
            setOneTimeFees(response.data || []);
        } catch (err) {
            console.error('Error fetching one-time fees:', err);
        }
    };

    // Fetch discounts
    const fetchDiscounts = async () => {
        try {
            const response = await discountAPI.getAll();
            setDiscounts(response.data || []);
        } catch (err) {
            console.error('Error fetching discounts:', err);
        }
    };

    // Fetch available add-ons
    const fetchAvailableAddOns = async () => {
        if (!id) return;
        try {
            // Admin view - get all active add-ons (not filtered by member availability)
            const response = await addOnAPI.getAll(true);
            setAvailableAddOns(response.data || []);
        } catch (err) {
            console.error('Error fetching available add-ons:', err);
        }
    };

    // Fetch member's current add-ons
    const fetchMemberAddOns = async () => {
        if (!id) return;
        try {
            const response = await addOnAPI.getMemberAddOns(id);
            setMemberAddOns(response.data || []);
        } catch (err) {
            console.error('Error fetching member add-ons:', err);
        }
    };

    const fetchGroupMembers = async (groupId: number) => {
        try {
            const membersResponse = await memberGroupAPI.getMembers(groupId);
            setGroupMembers(membersResponse.data || []);
        } catch (error) {
            console.error('Error fetching group members:', error);
        }
    };

    const fetchPrimaryContact = async (primaryContactId: string) => {
        try {
            const contactResponse = await memberProfileAPI.getMemberProfileById(primaryContactId);
            setPrimaryContact(contactResponse.data);
        } catch (error) {
            console.error('Error fetching primary contact:', error);
        }
    };

    const fetchMemberProfile = async () => {
        if (!id) return;

        setLoading(true);
        try {
            const response = await memberProfileAPI.getMemberProfileById(id);
            const profileData = response.data;
            setMemberProfile(profileData);

            // Set edit form with current member profile data
            setEditForm({
                firstName: profileData.firstName || '',
                lastName: profileData.lastName || '',
                email: profileData.email || '',
                phone: profileData.phone || '',
                mobile: profileData.mobile || '',
                address: profileData.address || '',
                city: profileData.city || '',
                state: profileData.state || '',
                zipCode: profileData.zipCode || '',
                country: profileData.country || '',
                dateOfBirth: profileData.dateOfBirth || '',
                membershipTierId: profileData.membershipTierId || '',
                startDate: profileData.startDate || '',
                status: profileData.status || '',
                monthlyPrice: profileData.monthlyPrice?.toString() || '',
                autoRenew: profileData.autoRenew || false,
                membershipNumber: profileData.membershipNumber || '',
                notes: profileData.notes || '',
                emailUpdates: profileData.emailUpdates !== undefined ? profileData.emailUpdates : true
            });

            // If the profile references a scheduled change id, fetch its details
            if (profileData.pendingScheduledChangeId) {
                try {
                    const scRes = await memberProfileAPI.getScheduledMembershipChange(id);
                    setPendingScheduledChange(scRes.data);
                } catch (err) {
                    console.error('Error fetching scheduled change during profile load:', err);
                    setPendingScheduledChange(null);
                }
            } else {
                setPendingScheduledChange(null);
            }
            // Initialize selected fees and discounts from the profile
            if (profileData.selectedFees && Array.isArray(profileData.selectedFees)) {
                // Transform the selectedFees to match the OneTimeFee interface
                const transformedFees = profileData.selectedFees
                    .filter((fee: any) => fee != null)
                    .map((fee: any, index: number) => ({
                        // For fees with null id, generate a temporary one for display purposes
                        // This handles legacy data that was stored without proper IDs
                        id: fee.id != null ? fee.id : `legacy-${fee.feeType}-${index}`,
                        feeName: fee.feeName,
                        amount: fee.amount,
                        feeType: fee.feeType,
                        // Note: transactionId is stored in the backend but not needed for the frontend OneTimeFee interface
                    }));
                
                setSelectedFees(transformedFees);
            } else {
                setSelectedFees([]);
            }

            if (profileData.selectedDiscounts && Array.isArray(profileData.selectedDiscounts)) {
                // Filter out null values and ensure valid discount objects
                const validDiscounts = profileData.selectedDiscounts.filter((discount: any) => discount != null && discount.id != null);
                setSelectedDiscounts(validDiscounts);
            } else {
                setSelectedDiscounts([]);
            }

            // If member profile has a group ID, fetch the group details
            if (profileData.memberGroupId) {
                try {
                    const groupResponse = await memberGroupAPI.getById(profileData.memberGroupId);
                    setMemberGroup(groupResponse.data);
                } catch (groupError) {
                    console.error('Error fetching member group:', groupError);
                    // Don't set error for group fetch failure, just log it
                }
            } else {
                setMemberGroup(null);
            }

            setError(null);
        } catch (err) {
            console.error('Error fetching member profile:', err);
            setError('Failed to load member profile. Please try again.');
        } finally {
            setLoading(false);
        }
    };

    // Handle edit dialog
    const handleEditOpen = () => {
        // Store the original fees when opening the edit dialog so we can detect changes
        setOriginalSelectedFees([...selectedFees]);
        setEditDialogOpen(true);
    };

    const handleEditClose = () => {
        setEditDialogOpen(false);
    };

    // Helper function to detect if fees have been modified
    const haveFeesChanged = (): boolean => {
        if (selectedFees.length !== originalSelectedFees.length) return true;
        
        // Check if the sets of fee IDs are different
        const currentFeeIds = new Set(selectedFees.map(f => f.id));
        const originalFeeIds = new Set(originalSelectedFees.map(f => f.id));
        
        if (currentFeeIds.size !== originalFeeIds.size) return true;
        
        for (const id of currentFeeIds) {
            if (!originalFeeIds.has(id)) return true;
        }
        
        return false;
    };

    const checkMembershipPaymentStatus = async () => {
        if (!id) return;
        
        setCheckingCancellation(true);
        try {
            // Get the first day of current month as YYYY-MM-DD string
            const now = new Date();
            const year = now.getFullYear();
            const month = now.getMonth() + 1; // 0-indexed, so add 1
            const firstDayOfMonthStr = `${year}-${month.toString().padStart(2, '0')}-01`;
            
            // Fetch membership payment transactions for current month
            const response = await transactionAPI.getMemberProfileTransactions(id, {
                type: 'membership_payment',
                limit: 100
            });
            
            let transactions: Transaction[] = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    transactions = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    transactions = response.data.data;
                } else if (response.data.transactions && Array.isArray(response.data.transactions)) {
                    transactions = response.data.transactions;
                }
            }
            
            // Check if there's a completed payment for current month
            // transactionDate is stored as DATE (YYYY-MM-DD), so use string comparison
            const completedThisMonth = transactions.find(tx => {
                const txDateStr = tx.transactionDate;
                if (!txDateStr) return false;
                
                // String comparison works for YYYY-MM-DD format dates
                return tx.status === 'completed' && txDateStr >= firstDayOfMonthStr;
            });
            
            if (completedThisMonth) {
                // Has completed payment - schedule cancellation for end of month
                setCancellationType('scheduled');
            } else {
                // No completed payment - allow immediate cancellation
                setCancellationType('immediate');
            }
            
            setCancellationConfirmOpen(true);
        } catch (err) {
            console.error('Error checking payment status:', err);
            setError('Failed to check payment status. Please try again.');
        } finally {
            setCheckingCancellation(false);
        }
    };

    const handleCancellationConfirm = () => {
        // User confirmed the cancellation - update the form
        if (pendingStatusChange) {
            setEditForm(prev => ({
                ...prev,
                status: pendingStatusChange
            }));
        }
        setCancellationConfirmOpen(false);
        setPendingStatusChange(null);
        setCancellationType(null);
    };

    const handleCancellationCancel = () => {
        // User cancelled the cancellation - reset
        setCancellationConfirmOpen(false);
        setPendingStatusChange(null);
        setCancellationType(null);
    };

    const handleEditSubmit = async () => {
        if (!id || !memberProfile) return;

        try {
            // Check if membership tier is being changed
            if (editForm.membershipTierId && editForm.membershipTierId !== memberProfile.membershipTierId) {
                const currentTier = membershipTiers.find(t => t.id === memberProfile.membershipTierId);
                const newTier = membershipTiers.find(t => t.id === editForm.membershipTierId);

                if (currentTier && newTier) {
                    const currentPrice = Number(currentTier.monthlyPrice);
                    const newPrice = Number(newTier.monthlyPrice);

                    if (newPrice < currentPrice) {
                        // This is a downgrade - show confirmation dialog
                        const nextMonth = new Date();
                        nextMonth.setMonth(nextMonth.getMonth() + 1);
                        nextMonth.setDate(1);

                        setDowngradeTierInfo({
                            currentTierName: currentTier.name,
                            newTierName: newTier.name,
                            currentPrice: currentPrice,
                            newPrice: newPrice,
                            effectiveDate: formatDate(nextMonth.toISOString())
                        });
                        setDowngradeConfirmOpen(true);
                        return; // Stop here and wait for user confirmation
                    } else if (newPrice > currentPrice) {
                        // This is an upgrade - show confirmation dialog with prorated charge
                        const today = new Date();
                        const daysInMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0).getDate();
                        const daysRemaining = daysInMonth - today.getDate() + 1;
                        const proratedCharge = (newPrice - currentPrice) * (daysRemaining / daysInMonth);

                        setUpgradeTierInfo({
                            currentTierName: currentTier.name,
                            newTierName: newTier.name,
                            currentPrice: currentPrice,
                            newPrice: newPrice,
                            proratedCharge: Math.round(proratedCharge * 100) / 100
                        });
                        setUpgradeConfirmOpen(true);
                        return; // Stop here and wait for user confirmation
                    }
                }
            }

            // Continue with normal update
            await executeEdit();
        } catch (err: any) {
            console.error('Error updating member profile:', err);
            console.error('Error response:', err.response?.data);
            setError(err.response?.data?.message || 'Failed to update member profile. Please try again.');
        }
    };

    const executeEdit = async () => {
        if (!id || !memberProfile) return;

        try {
            // Build the update data - ONLY include fields that are actually being edited
            const updateData: any = {
                firstName: editForm.firstName,
                lastName: editForm.lastName,
                phone: editForm.phone,
                mobile: editForm.mobile,
                address: editForm.address,
                city: editForm.city,
                state: editForm.state,
                zipCode: editForm.zipCode,
                country: editForm.country,
                dateOfBirth: editForm.dateOfBirth,
                membershipTierId: editForm.membershipTierId,
                startDate: editForm.startDate,
                status: editForm.status as MembershipStatus,
                monthlyPrice: editForm.monthlyPrice ? parseFloat(editForm.monthlyPrice) : undefined,
                autoRenew: editForm.autoRenew,
                membershipNumber: editForm.membershipNumber,
                notes: editForm.notes,
                emailUpdates: editForm.emailUpdates
            };

            // ONLY include selectedFees if the user actually modified fees in the edit dialog
            // This prevents accidental removal of fees (especially legacy fees with null IDs)
            // when the user is just updating other profile fields
            if (haveFeesChanged()) {
                // Filter out legacy temporary IDs before sending to backend
                updateData.selectedFees = selectedFees
                    .filter(fee => !fee.id?.toString().startsWith('legacy-'))
                    .map(fee => ({
                        id: fee.id,
                        feeName: fee.feeName,
                        amount: fee.amount,
                        feeType: fee.feeType
                    }));
            }
            // NOTE: selectedDiscounts is intentionally NOT included here
            // - Discounts are managed through separate add/remove discount actions
            // - Backend only processes discounts when this field is explicitly provided

            const response = await memberProfileAPI.updateMemberProfile(id, updateData);
            
            // Check if this was a scheduled cancellation
            if (response.data?.scheduledCancellation && response.data?.message) {
                setScheduledDowngradeMessage(response.data.message);
                setScheduledDowngradeDialogOpen(true);
            }
            // Check if this was a scheduled downgrade
            else if (response.data?.scheduledDowngrade && response.data?.message) {
                setScheduledDowngradeMessage(response.data.message);
                setScheduledDowngradeDialogOpen(true);
            } else {
                setSuccess('Member profile updated successfully!');
                // Clear success message after 5 seconds
                setTimeout(() => {
                    setSuccess(null);
                }, 5000);
            }
            
            setEditDialogOpen(false);
            fetchMemberProfile(); // Refresh data
        } catch (err: any) {
            console.error('Error updating member profile:', err);
            console.error('Error response:', err.response?.data);
            setError(err.response?.data?.message || 'Failed to update member profile. Please try again.');
        }
    };

    const handleEditChange = async (field: string, value: string | boolean) => {
        // If changing status to cancelled, check payment status first
        if (field === 'status' && value === 'cancelled' && editForm.status !== 'cancelled') {
            setPendingStatusChange('cancelled');
            await checkMembershipPaymentStatus();
            return; // Don't update form yet - wait for confirmation
        }
        
        // If changing membership tier, auto-populate the monthly price
        if (field === 'membershipTierId' && typeof value === 'string') {
            const selectedTier = membershipTiers.find(tier => tier.id === value);
            if (selectedTier) {
                setEditForm(prev => ({
                    ...prev,
                    [field]: value,
                    monthlyPrice: selectedTier.monthlyPrice.toString() // Set the tier's default price
                }));
                return;
            }
        }

        setEditForm(prev => ({
            ...prev,
            [field]: value
        }));
    };

    // Fee and discount management functions
    const handleFeeDialogToggle = () => {
        setFeeDialogOpen(!feeDialogOpen);
    };

    const handleDiscountDialogToggle = () => {
        setDiscountDialogOpen(!discountDialogOpen);
    };

    const handleAddFee = (fee: OneTimeFee) => {
        setSelectedFees([...selectedFees, fee]);
        setFeeDialogOpen(false);
    };

    const handleAddDiscount = async (discount: Discount) => {
        if (!id) return;

        // Close the selection dialog first
        setDiscountDialogOpen(false);

        try {
            // Fetch pending membership fee transactions (NOT initiation fees or other types)
            const response = await transactionAPI.getMemberProfileTransactions(id, {
                type: 'membership_payment',
                status: 'pending'
            });

            let pendingTxs: Transaction[] = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    pendingTxs = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    pendingTxs = response.data.data;
                } else if (response.data.transactions && Array.isArray(response.data.transactions)) {
                    pendingTxs = response.data.transactions;
                }
            }

            if (pendingTxs.length > 0) {
                // Show discount application dialog to ask if they want to apply to existing pending transactions
                setPendingMembershipTransactions(pendingTxs);
                setPendingDiscountAction({ action: 'add', discount });
                setDiscountApplicationDialogOpen(true);
                // Add to local state immediately so UI updates
                setSelectedDiscounts([...selectedDiscounts, discount]);
            } else {
                // No pending transactions - persist the discount directly to the backend
                // It will apply to future transactions only
                const updatedDiscounts = [...selectedDiscounts, discount];
                
                try {
                    await memberProfileAPI.updateMemberProfile(id, {
                        selectedDiscounts: updatedDiscounts.map(d => d.id)
                    });
                    
                    setSelectedDiscounts(updatedDiscounts);
                    setSuccess('Discount added successfully. It will apply to future transactions.');
                    setTimeout(() => setSuccess(null), 5000);
                } catch (updateErr: any) {
                    console.error('Error persisting discount:', updateErr);
                    setError(updateErr.response?.data?.message || 'Failed to add discount. Please try again.');
                    setTimeout(() => setError(null), 5000);
                }
            }
        } catch (err) {
            console.error('Error fetching pending transactions:', err);
            setError('Failed to add discount. Please try again.');
            setTimeout(() => setError(null), 5000);
        }
    };

    const handleRemoveFee = (feeId: number | string) => {
        setSelectedFees(selectedFees.filter(fee => fee != null && fee.id !== feeId));
    };

    const handleRemoveDiscount = async (discountId: string) => {
        if (!id) return;

        const discountToRemove = selectedDiscounts.find(d => d.id === discountId);
        if (!discountToRemove) return;

        try {
            // Fetch pending membership fee transactions (NOT initiation fees or other types)
            const response = await transactionAPI.getMemberProfileTransactions(id, {
                type: 'membership_payment',
                status: 'pending'
            });

            let pendingTxs: Transaction[] = [];
            if (response.data) {
                if (Array.isArray(response.data)) {
                    pendingTxs = response.data;
                } else if (response.data.data && Array.isArray(response.data.data)) {
                    pendingTxs = response.data.data;
                } else if (response.data.transactions && Array.isArray(response.data.transactions)) {
                    pendingTxs = response.data.transactions;
                }
            }

            if (pendingTxs.length > 0) {
                // Show discount application dialog to ask if they want to remove from existing pending transactions
                setPendingMembershipTransactions(pendingTxs);
                setPendingDiscountAction({ action: 'remove', discount: discountToRemove });
                setDiscountApplicationDialogOpen(true);
                // Remove from local state immediately so UI updates
                setSelectedDiscounts(selectedDiscounts.filter(discount => discount != null && discount.id !== discountId));
            } else {
                // No pending transactions - persist the discount removal directly to the backend
                // It will affect future transactions only
                const updatedDiscounts = selectedDiscounts.filter(discount => discount != null && discount.id !== discountId);
                
                try {
                    await memberProfileAPI.updateMemberProfile(id, {
                        selectedDiscounts: updatedDiscounts.map(d => d.id)
                    });
                    
                    setSelectedDiscounts(updatedDiscounts);
                    setSuccess('Discount removed successfully. Future transactions will not include this discount.');
                    setTimeout(() => setSuccess(null), 5000);
                } catch (updateErr: any) {
                    console.error('Error persisting discount removal:', updateErr);
                    setError(updateErr.response?.data?.message || 'Failed to remove discount. Please try again.');
                    setTimeout(() => setError(null), 5000);
                }
            }
        } catch (err) {
            console.error('Error fetching pending transactions:', err);
            setError('Failed to remove discount. Please try again.');
            setTimeout(() => setError(null), 5000);
        }
    };

    // Add-on management functions
    const handleAddOnDialogToggle = () => {
        setAddOnDialogOpen(!addOnDialogOpen);
    };

    const handleShowPreview = async (addOnId: string) => {
        if (!id) return;
        setAddOnLoading(true);
        setSelectedAddOnId(addOnId);
        try {
            const response = await addOnAPI.previewSubscription(id, addOnId);
            setPreviewData(response.data);
            setAddOnDialogOpen(false);
            setPreviewDialogOpen(true);
        } catch (err: any) {
            console.error('Error loading preview:', err);
            setError(err.response?.data?.message || 'Failed to load subscription preview.');
        } finally {
            setAddOnLoading(false);
        }
    };

    const handleConfirmSubscription = async () => {
        if (!id || !selectedAddOnId) return;
        setAddOnLoading(true);
        try {
            await addOnAPI.subscribe(id, selectedAddOnId);
            await fetchMemberAddOns();
            setPreviewDialogOpen(false);
            setPreviewData(null);
            setSelectedAddOnId(null);
            setSuccess('Add-on subscribed successfully!');
            setTimeout(() => setSuccess(null), 5000);
        } catch (err: any) {
            console.error('Error subscribing to add-on:', err);
            setError(err.response?.data?.message || 'Failed to subscribe to add-on. Please try again.');
            setPreviewDialogOpen(false);
        } finally {
            setAddOnLoading(false);
        }
    };

    const handleRemoveAddOn = async (memberAddOnId: string, addOnId: string) => {
        if (!id) return;
        setAddOnLoading(true);
        try {
            await addOnAPI.unsubscribe(id, addOnId);
            await fetchMemberAddOns();
            setSuccess('Add-on will be removed at the end of the current billing period.');
            setTimeout(() => setSuccess(null), 5000);
        } catch (err: any) {
            console.error('Error unsubscribing from add-on:', err);
            setError(err.response?.data?.message || 'Failed to unsubscribe from add-on. Please try again.');
        } finally {
            setAddOnLoading(false);
        }
    };

    // Discount application dialog handlers
    const handleDiscountApplicationCancel = () => {
        setDiscountApplicationDialogOpen(false);
        setPendingDiscountAction(null);
        setPendingMembershipTransactions([]);
    };

    const handleDiscountApplicationApplyToFuture = async () => {
        if (!pendingDiscountAction || !id || !memberProfile) return;

        const { action, discount } = pendingDiscountAction;
        setDiscountApplicationLoading(true);

        try {
            let updatedDiscounts: Discount[];
            if (action === 'add') {
                updatedDiscounts = [...selectedDiscounts, discount];
            } else {
                updatedDiscounts = selectedDiscounts.filter(d => d.id !== discount.id);
            }

            // CRITICAL: Do NOT send selectedFees when only updating discounts
            // The backend will treat any fee changes as intentional removal/addition
            // which could cancel transactions for legacy fees (fees with null IDs)
            const updateData = {
                firstName: memberProfile.firstName,
                lastName: memberProfile.lastName,
                phone: memberProfile.phone,
                mobile: memberProfile.mobile,
                address: memberProfile.address,
                city: memberProfile.city,
                state: memberProfile.state,
                zipCode: memberProfile.zipCode,
                country: memberProfile.country,
                dateOfBirth: memberProfile.dateOfBirth,
                membershipTierId: memberProfile.membershipTierId,
                startDate: memberProfile.startDate,
                status: memberProfile.status,
                monthlyPrice: memberProfile.monthlyPrice,
                autoRenew: memberProfile.autoRenew,
                membershipNumber: memberProfile.membershipNumber,
                notes: memberProfile.notes,
                emailUpdates: memberProfile.emailUpdates,
                // selectedFees: NOT INCLUDED - we're only updating discounts
                selectedDiscounts: updatedDiscounts.filter(d => d != null && d.id != null).map(d => d.id)
            };

            // Save member profile with updated discounts
            await memberProfileAPI.updateMemberProfile(id, updateData);

            // Update local state
            setSelectedDiscounts(updatedDiscounts);

            setSuccess(`Discount ${action === 'add' ? 'added' : 'removed'}. It will ${action === 'add' ? 'apply to' : 'not apply to'} future transactions only.`);
            setTimeout(() => setSuccess(null), 5000);

            // Refresh member profile data
            await fetchMemberProfile();

            handleDiscountApplicationCancel();
        } catch (err: any) {
            console.error('Error updating member profile:', err);
            setError(err.response?.data?.message || 'Failed to update member profile. Please try again.');
        } finally {
            setDiscountApplicationLoading(false);
        }
    };

    const handleDiscountApplicationApplyToExisting = async () => {
        if (!pendingDiscountAction || !id || !memberProfile) return;

        const { action, discount } = pendingDiscountAction;
        setDiscountApplicationLoading(true);

        try {
            // First, update the member profile's selected discounts on the backend
            let updatedDiscounts: Discount[];
            if (action === 'add') {
                updatedDiscounts = [...selectedDiscounts, discount];
            } else {
                updatedDiscounts = selectedDiscounts.filter(d => d.id !== discount.id);
            }

            // CRITICAL: Do NOT send selectedFees when only updating discounts
            // The backend will treat any fee changes as intentional removal/addition
            // which could cancel transactions for legacy fees (fees with null IDs)
            const updateData = {
                firstName: memberProfile.firstName,
                lastName: memberProfile.lastName,
                phone: memberProfile.phone,
                mobile: memberProfile.mobile,
                address: memberProfile.address,
                city: memberProfile.city,
                state: memberProfile.state,
                zipCode: memberProfile.zipCode,
                country: memberProfile.country,
                dateOfBirth: memberProfile.dateOfBirth,
                membershipTierId: memberProfile.membershipTierId,
                startDate: memberProfile.startDate,
                status: memberProfile.status,
                monthlyPrice: memberProfile.monthlyPrice,
                autoRenew: memberProfile.autoRenew,
                membershipNumber: memberProfile.membershipNumber,
                notes: memberProfile.notes,
                emailUpdates: memberProfile.emailUpdates,
                // selectedFees: NOT INCLUDED - we're only updating discounts
                selectedDiscounts: updatedDiscounts.filter(d => d != null && d.id != null).map(d => d.id)
            };

            // Save member profile with updated discounts
            await memberProfileAPI.updateMemberProfile(id, updateData);

            // Update local state
            setSelectedDiscounts(updatedDiscounts);

            // Now apply the discount change to pending transactions
            const transactionIds = pendingMembershipTransactions.map(tx => tx.id);
            await transactionAPI.applyDiscountToPendingTransactions(id, {
                discountAction: action,
                transactionIds
            });

            setSuccess(`Discount ${action === 'add' ? 'applied to' : 'removed from'} ${pendingMembershipTransactions.length} pending transaction(s).`);
            setTimeout(() => setSuccess(null), 5000);

            // Refresh member profile data
            await fetchMemberProfile();

            // Refresh transactions if we're on that tab
            if (currentTab === 2) {
                fetchTransactions();
            }

            handleDiscountApplicationCancel();
        } catch (err: any) {
            console.error('Error applying discount to transactions:', err);
            setError(err.response?.data?.message || 'Failed to update transactions. Please try again.');
        } finally {
            setDiscountApplicationLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setCurrentTab(newValue);
    };

    const handleAuditLogPageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setAuditLogsPagination(prev => ({
            ...prev,
            page
        }));
    };

    const handleTransactionPageChange = (event: React.ChangeEvent<unknown>, page: number) => {
        setTransactionsPagination(prev => ({
            ...prev,
            page
        }));
    };

    const handleExportTransactions = () => {
        // Helper function to escape and quote CSV fields
        const escapeCSVField = (field: string): string => {
            // Convert to string and escape double quotes by doubling them
            const fieldStr = String(field || '');
            const escaped = fieldStr.replace(/"/g, '""');
            // Always encapsulate in double quotes
            return `"${escaped}"`;
        };

        const headers = ['Transaction Date', 'Created Date', 'Member Charged', 'Description', 'Type', 'Amount', 'Status'];

        const rows = transactions.map(transaction => {
            const memberName = transaction.memberProfile
                ? `${transaction.memberProfile.firstName} ${transaction.memberProfile.lastName}`
                : 'N/A';

            return [
                formatDate(transaction.transactionDate || transaction.createdAt),
                formatDate(transaction.createdAt),
                memberName,
                transaction.description || '',
                transaction.type,
                `$${transaction.totalAmount || transaction.amount}`,
                transaction.status
            ];
        });

        // Encapsulate all headers and row fields in double-quotes
        const csvContent = [
            headers.map(escapeCSVField).join(','),
            ...rows.map(row => row.map(escapeCSVField).join(','))
        ].join('\n');

        const blob = new Blob([csvContent], { type: 'text/csv' });
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `transactions-${memberProfile?.firstName}-${memberProfile?.lastName}-${formatDate(new Date().toISOString())}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
    };

    const handleDownloadInvoice = async (transaction: Transaction, event: React.MouseEvent) => {
        event.stopPropagation();
        
        if (transaction.status !== 'completed') {
            setError('Invoice is only available for completed transactions');
            return;
        }

        try {
            const response = await paymentMethodAPI.getInvoice(transaction.id);
            const invoiceData = response.data;

            // Prepare member info with address if available
            const memberInfo = memberProfile ? {
                name: `${memberProfile.firstName} ${memberProfile.lastName}`,
                email: memberProfile.email,
                address: (memberProfile as any).address,
                city: (memberProfile as any).city,
                state: (memberProfile as any).state,
                zipCode: (memberProfile as any).zipCode,
                country: (memberProfile as any).country
            } : undefined;

            // Generate invoice HTML using shared utility
            const invoiceHTML = generateInvoiceHTML({
                invoiceData,
                memberInfo,
                tenantSettings,
                transactionId: transaction.id
            });
            
            // Open invoice in new window
            openInvoiceWindow(invoiceHTML);
        } catch (error: any) {
            console.error('Error downloading invoice:', error);
            setError(error?.response?.data?.message || 'Failed to download invoice');
        }
    };

    const handleTransactionClick = (transaction: Transaction) => {
        setSelectedTransaction(transaction);
        setAdminNote('');
        setTransactionDetailsOpen(true);
    };

    const handleCloseTransactionDialog = () => {
        setTransactionDetailsOpen(false);
        setSelectedTransaction(null);
        setAdminNote('');
    };

    // Handle open cancel confirmation
    const handleOpenCancelConfirm = () => {
        if (!adminNote.trim()) {
            setError('Please provide a note explaining the cancellation reason.');
            return;
        }
        setCancelConfirmOpen(true);
    };

    // Handle cancel transaction
    const handleCancelTransaction = async () => {
        if (!selectedTransaction || !adminNote.trim()) {
            setError('Cannot cancel transaction: note is required.');
            return;
        }

        setProcessingAction(true);
        try {
            await transactionAPI.update(selectedTransaction.id, {
                status: 'cancelled',
                adminNote: adminNote.trim()
            });

            setSuccess('Transaction cancelled successfully.');
            setCancelConfirmOpen(false);
            setTransactionDetailsOpen(false);
            setSelectedTransaction(null);
            setAdminNote('');

            // Refresh transactions list
            fetchTransactions();

            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccess(null);
            }, 5000);
        } catch (err: any) {
            console.error('Error cancelling transaction:', err);
            setError(err.response?.data?.message || 'Failed to cancel transaction. Please try again.');
            setCancelConfirmOpen(false);
        } finally {
            setProcessingAction(false);
        }
    };

    // Handle open refund confirmation
    const handleOpenRefundConfirm = () => {
        if (!adminNote.trim()) {
            setError('Please provide a note for the refund');
            return;
        }
        setRefundConfirmOpen(true);
    };

    // Handle refund transaction
    const handleRefundTransaction = async () => {
        if (!selectedTransaction || !adminNote.trim()) {
            setError('Admin note is required');
            return;
        }

        setProcessingAction(true);
        try {
            await transactionAPI.refund(selectedTransaction.id, { adminNote: adminNote.trim() });
            setSuccess('Transaction refunded successfully');
            setRefundConfirmOpen(false);
            setTransactionDetailsOpen(false);
            setSelectedTransaction(null);
            setAdminNote('');
            
            // Refresh transactions and member profile
            await fetchTransactions();
            await fetchMemberProfile();

            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccess(null);
            }, 5000);
        } catch (err: any) {
            console.error('Error refunding transaction:', err);
            setErrorDialogMessage(err.response?.data?.message || 'Failed to refund transaction');
            setErrorDialogOpen(true);
            setRefundConfirmOpen(false);
        } finally {
            setProcessingAction(false);
        }
    };

    // Handle retry payment for all failed transactions
    const handleRetryPayment = async () => {
        if (!id) return;

        setRetryPaymentProcessing(true);
        setRetryPaymentResults([]);

        try {
            // Get all failed transactions for this member
            const failedTransactions = transactions.filter(
                tx => tx.status === 'failed'
            );

            if (failedTransactions.length === 0) {
                setRetryPaymentResults([{
                    transactionId: 'none',
                    success: false,
                    message: 'No failed transactions to retry'
                }]);
                setRetryPaymentProcessing(false);
                return;
            }

            const results: Array<{
                transactionId: string;
                success: boolean;
                message: string;
                type?: string;
                amount?: number;
            }> = [];

            // Process each transaction individually to track results
            for (const transaction of failedTransactions) {
                try {
                    const response = await paymentMethodAPI.processTransaction(transaction.id);
                    
                    results.push({
                        transactionId: transaction.id,
                        success: true,
                        message: response.data.message || 'Payment processed successfully',
                        type: transaction.type,
                        amount: transaction.totalAmount || transaction.amount
                    });
                } catch (err: any) {
                    const errorMessage = err?.response?.data?.message || 'Failed to process payment';
                    
                    results.push({
                        transactionId: transaction.id,
                        success: false,
                        message: errorMessage,
                        type: transaction.type,
                        amount: transaction.totalAmount || transaction.amount
                    });
                }
            }

            setRetryPaymentResults(results);
            
            // Refresh transactions to show updated statuses
            await fetchTransactions();
            await fetchMemberProfile();
        } catch (err: any) {
            setRetryPaymentResults([{
                transactionId: 'error',
                success: false,
                message: err?.message || 'An unexpected error occurred'
            }]);
        } finally {
            setRetryPaymentProcessing(false);
        }
    };

    // Handle close retry payment dialog
    const handleCloseRetryPaymentDialog = () => {
        setRetryPaymentDialogOpen(false);
        setRetryPaymentResults([]);
    };

    // Scheduled membership change handlers
    const handleViewScheduledChange = async () => {
        if (!id || !memberProfile?.pendingScheduledChangeId) return;

        setScheduledChangeLoading(true);
        setScheduledChangeDialogOpen(true);

        try {
            const response = await memberProfileAPI.getScheduledMembershipChange(id);
            setScheduledChangeDetails(response.data);
        } catch (err: any) {
            console.error('Error fetching scheduled change:', err);
            setError(err.response?.data?.message || 'Failed to load scheduled change details.');
            setScheduledChangeDialogOpen(false);
        } finally {
            setScheduledChangeLoading(false);
        }
    };

    const handleCancelScheduledChange = async () => {
        if (!id || !memberProfile?.pendingScheduledChangeId) return;

        setCancelingScheduledChange(true);

        try {
            await memberProfileAPI.cancelScheduledMembershipChange(
                id,
                memberProfile.pendingScheduledChangeId
            );

            setSuccess('Scheduled membership change has been cancelled successfully.');
            setTimeout(() => setSuccess(null), 5000);
            
            setScheduledChangeDialogOpen(false);
            setScheduledChangeDetails(null);
            
            // Refresh member profile to update pending change status
            fetchMemberProfile();
        } catch (err: any) {
            console.error('Error cancelling scheduled change:', err);
            setError(err.response?.data?.message || 'Failed to cancel scheduled change.');
        } finally {
            setCancelingScheduledChange(false);
        }
    };

    // Profile picture handlers
    const handleProfilePictureClick = () => {
        setUploadOptionsOpen(true);
    };

    const handleUploadFromFile = () => {
        setUploadOptionsOpen(false);
        fileInputRef.current?.click();
    };

    const handleOpenCamera = async () => {
        setUploadOptionsOpen(false);
        setCameraDialogOpen(true);

        try {
            const stream = await navigator.mediaDevices.getUserMedia({
                video: {
                    width: { ideal: 1280 },
                    height: { ideal: 720 },
                    facingMode: 'user'
                }
            });
            setCameraStream(stream);

            // Attach stream to video element
            if (videoRef.current) {
                videoRef.current.srcObject = stream;
            }
        } catch (err) {
            console.error('Error accessing camera:', err);
            setError('Unable to access camera. Please check your camera permissions.');
            setCameraDialogOpen(false);
        }
    };

    const handleCloseCameraDialog = () => {
        // Stop camera stream
        if (cameraStream) {
            cameraStream.getTracks().forEach(track => track.stop());
            setCameraStream(null);
        }
        setCameraDialogOpen(false);
    };

    const handleCapturePhoto = () => {
        if (!videoRef.current || !canvasRef.current) return;

        const video = videoRef.current;
        const canvas = canvasRef.current;
        const context = canvas.getContext('2d');

        if (!context) return;

        // Set canvas dimensions to match video
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;

        // Draw video frame to canvas
        context.drawImage(video, 0, 0, canvas.width, canvas.height);

        // Convert canvas to blob
        canvas.toBlob((blob) => {
            if (!blob) return;

            // Create a file from the blob
            const file = new File([blob], 'camera-photo.jpg', { type: 'image/jpeg' });
            setOriginalFile(file);

            // Convert blob to data URL for preview
            const reader = new FileReader();
            reader.onload = () => {
                setImageSrc(reader.result as string);
                setCrop({
                    unit: '%',
                    width: 90,
                    height: 90,
                    x: 5,
                    y: 5
                });
                setZoom(1);
                setCompletedCrop(null);
                setCropDialogOpen(true);
            };
            reader.readAsDataURL(blob);

            // Close camera dialog and stop stream
            handleCloseCameraDialog();
        }, 'image/jpeg', 0.95);
    };

    const handleFileSelect = async (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        if (!file) return;

        // Validate file type
        const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png'];
        if (!allowedTypes.includes(file.type)) {
            setError('Invalid file type. Only JPEG, JPG, and PNG files are allowed.');
            return;
        }

        // Validate file size (2MB max)
        const maxSize = 2 * 1024 * 1024; // 2MB in bytes
        if (file.size > maxSize) {
            setError('File size too large. Maximum allowed size is 2MB.');
            return;
        }

        // Store the original file
        setOriginalFile(file);

        // Read the file and open crop dialog
        const reader = new FileReader();
        reader.onload = () => {
            setImageSrc(reader.result as string);
            setCropDialogOpen(true);
            // Reset crop and zoom
            setCrop({
                unit: '%',
                width: 90,
                height: 90,
                x: 5,
                y: 5
            });
            setZoom(1);
        };
        reader.readAsDataURL(file);

        // Reset file input
        if (fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleCropDialogClose = () => {
        setCropDialogOpen(false);
        setImageSrc(null);
        setOriginalFile(null);
        setCompletedCrop(null);
    };

    const handleImageLoad = (e: React.SyntheticEvent<HTMLImageElement>) => {
        const { width, height } = e.currentTarget;

        // Calculate the initial pixel crop based on the percentage crop
        const pixelCrop: PixelCrop = {
            unit: 'px',
            x: (crop.x / 100) * width,
            y: (crop.y / 100) * height,
            width: (crop.width / 100) * width,
            height: (crop.height / 100) * height
        };

        setCompletedCrop(pixelCrop);
    };

    const getCroppedImg = async (): Promise<Blob | null> => {
        if (!imgRef.current || !completedCrop) return null;

        const image = imgRef.current;
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');

        if (!ctx) return null;

        // Calculate the scaling factor between display size and natural size
        const scaleX = image.naturalWidth / (image.width * zoom);
        const scaleY = image.naturalHeight / (image.height * zoom);

        // Set canvas size to desired output size (fixed at 400x400 for profile pictures)
        const outputSize = 400;
        canvas.width = outputSize;
        canvas.height = outputSize;

        // Calculate source dimensions from the crop area
        const sourceX = completedCrop.x * scaleX;
        const sourceY = completedCrop.y * scaleY;
        const sourceWidth = completedCrop.width * scaleX;
        const sourceHeight = completedCrop.height * scaleY;

        // Draw the cropped and scaled image
        ctx.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            outputSize,
            outputSize
        );

        // Convert canvas to blob
        return new Promise((resolve) => {
            canvas.toBlob((blob) => {
                resolve(blob);
            }, originalFile?.type || 'image/jpeg', 0.95);
        });
    };

    const handleCropComplete = async () => {
        if (!id || !originalFile) return;

        setUploadingPicture(true);
        setError(null);

        try {
            // Get the cropped image blob
            const croppedBlob = await getCroppedImg();
            if (!croppedBlob) {
                throw new Error('Failed to crop image');
            }

            // Convert blob to file
            const croppedFile = new File(
                [croppedBlob],
                originalFile.name,
                { type: originalFile.type }
            );

            const response = await memberProfileAPI.uploadProfilePicture(id, croppedFile);
            setSuccess('Profile picture uploaded successfully!');

            // Update the member profile with the new picture URL
            if (memberProfile) {
                setMemberProfile({
                    ...memberProfile,
                    profilePictureUrl: response.data.profilePictureUrl
                });
            }

            // Fetch the new profile picture as blob
            try {
                const pictureResponse = await memberProfileAPI.getProfilePicture(id);
                const blob = new Blob([pictureResponse.data], { type: pictureResponse.headers['content-type'] || 'image/jpeg' });

                // Revoke old blob URL if exists
                if (profilePictureBlobUrl) {
                    URL.revokeObjectURL(profilePictureBlobUrl);
                }

                const blobUrl = URL.createObjectURL(blob);
                setProfilePictureBlobUrl(blobUrl);
            } catch (blobErr) {
                console.error('Error fetching uploaded picture:', blobErr);
            }

            // Close the crop dialog
            handleCropDialogClose();

            // Clear success message after 5 seconds
            setTimeout(() => {
                setSuccess(null);
            }, 5000);
        } catch (err: any) {
            console.error('Error uploading profile picture:', err);
            setError(err.response?.data?.message || 'Failed to upload profile picture. Please try again.');
        } finally {
            setUploadingPicture(false);
        }
    };

    const getChangedFieldsDisplay = (auditLog: AuditLog) => {
        if (!auditLog.changedFields || auditLog.changedFields.length === 0) {
            return 'No specific fields tracked';
        }

        const changes: string[] = [];

        auditLog.changedFields.forEach(field => {
            const oldValue = auditLog.previousValues?.[field];
            const newValue = auditLog.newValues?.[field];

            // Format the field name for display
            const displayField = field.replace(/([A-Z])/g, ' $1').replace(/^./, str => str.toUpperCase());

            if (oldValue !== undefined && newValue !== undefined) {
                changes.push(`${displayField}: "${oldValue}" → "${newValue}"`);
            } else if (newValue !== undefined) {
                changes.push(`${displayField}: Added "${newValue}"`);
            } else if (oldValue !== undefined) {
                changes.push(`${displayField}: Removed "${oldValue}"`);
            }
        });

        return changes.length > 0 ? changes.join(', ') : 'No changes detailed';
    };

    const getActionColor = (action: string) => {
        switch (action) {
            case 'create': return 'success';
            case 'update': return 'info';
            case 'delete': return 'error';
            case 'status_change': return 'warning';
            default: return 'default';
        }
    };

    // Get status color based on membership status
    const getStatusColor = (status: MembershipStatus) => {
        switch (status) {
            case 'active': return 'success';
            case 'pending': return 'warning';
            case 'expired': return 'warning';
            case 'cancelled': return 'error';
            case 'suspended': return 'error';
            case 'on_hold': return 'info';
            default: return 'default';
        }
    };

    // Get transaction status color
    const getTransactionStatusColor = (status: string) => {
        switch (status) {
            case 'completed': return 'success';
            case 'pending': return 'warning';
            case 'failed': return 'error';
            case 'cancelled': return 'default';
            default: return 'default';
        }
    };

    // Get transaction type color
    const getTransactionTypeColor = (type: string) => {
        switch (type) {
            case 'charge': return 'error';
            case 'payment': return 'success';
            case 'refund': return 'info';
            default: return 'default';
        }
    };

    // Format currency
    const formatCurrency = (amount: number | undefined) => {
        if (amount === undefined || amount === null) return 'N/A';
        return `$${Number(amount).toFixed(2)}`;
    };

    // Check if start date is in the past (read-only check)
    const isStartDateReadOnly = () => {
        if (!editForm.startDate) return false;

        // Extract date string (YYYY-MM-DD format)
        const dateStr = editForm.startDate.split('T')[0];
        const [year, month, day] = dateStr.split('-').map(Number);

        // Create date objects in local timezone
        const startDate = new Date(year, month - 1, day); // Month is 0-indexed
        const today = new Date();
        today.setHours(0, 0, 0, 0); // Set to beginning of day for accurate comparison

        return startDate < today;
    };

    if (loading) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            </Container>
        );
    }

    if (error || !memberProfile) {
        return (
            <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
                <Alert severity="error" sx={{ mb: 2 }}>
                    {error || 'Member profile not found.'}
                </Alert>
                <Button
                    variant="outlined"
                    startIcon={<ArrowBackIcon />}
                    onClick={() => navigate('/member-management')}
                >
                    Back to Member Management
                </Button>
            </Container>
        );
    }

    return (
        <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
            {/* Breadcrumbs */}
            <Breadcrumbs
                separator={<NavigateNextIcon fontSize="small" />}
                aria-label="breadcrumb"
                sx={{ mb: 3 }}
            >
                <RouterLink
                    className='user-link'
                    style={{ display: 'flex', alignItems: 'center' }}
                    color="inherit"
                    to="/dashboard"
                >
                    <HomeIcon sx={{ mr: 0.5 }} fontSize="inherit" />
                    Dashboard
                </RouterLink>
                <RouterLink
                    className='user-link'
                    style={{ display: 'flex', alignItems: 'center' }}
                    color="inherit"
                    to="/member-management"
                >
                    Member Management
                </RouterLink>
                <Typography color="text.primary">
                    {memberProfile.firstName} {memberProfile.lastName}
                </Typography>
            </Breadcrumbs>

            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <IconButton onClick={() => navigate('/member-management')} sx={{ mr: 2 }}>
                        <ArrowBackIcon />
                    </IconButton>
                    <Typography variant="h4" component="h1">
                        Member Profile Details
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1 }}>
                    <IconButton
                        color="primary"
                        onClick={handleEditOpen}
                        title="Edit Member Profile"
                    >
                        <EditIcon />
                    </IconButton>
                </Box>
            </Box>

            {/* Success/Error Messages */}
            {success && (
                <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
                    {success}
                </Alert>
            )}
            {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                    {error}
                </Alert>
            )}

            {/* Tabs */}
            <Box sx={{ width: '100%' }}>
                <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
                    <Tabs value={currentTab} onChange={handleTabChange} aria-label="member profile tabs">
                        <Tab
                            label="Information"
                            icon={<InfoIcon />}
                            iconPosition="start"
                            {...a11yProps(0)}
                        />
                        <Tab
                            label="History"
                            icon={<HistoryIcon />}
                            iconPosition="start"
                            {...a11yProps(1)}
                        />
                        <Tab
                            label="Transactions"
                            icon={<PaymentIcon />}
                            iconPosition="start"
                            {...a11yProps(2)}
                        />
                        <Tab
                            label="Billing"
                            icon={<CreditCardIcon />}
                            iconPosition="start"
                            {...a11yProps(3)}
                        />
                        <Tab
                            label="Onboarding"
                            icon={<AssignmentIcon />}
                            iconPosition="start"
                            {...a11yProps(4)}
                        />
                        <Tab
                            label="PT Packs"
                            icon={<FitnessCenterIcon />}
                            iconPosition="start"
                            {...a11yProps(5)}
                        />
                    </Tabs>
                </Box>

                {/* Information Tab */}
                <TabPanel value={currentTab} index={0}>
                    <Grid container spacing={3}>
                        {/* Top Row: Picture and Personal Info */}
                        <Grid size={{ xs: 12 }}>
                            <Card>
                                <CardContent>
                                    <Grid container spacing={3}>
                                        {/* Picture Placeholder */}
                                        <Grid size={{ xs: 12, sm: 3, md: 2 }}>
                                            <Box
                                                sx={{
                                                    width: '100%',
                                                    aspectRatio: '1',
                                                    backgroundColor: 'grey.200',
                                                    borderRadius: 2,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    border: '2px dashed',
                                                    borderColor: 'grey.400',
                                                    maxWidth: 150,
                                                    mx: 'auto',
                                                    position: 'relative',
                                                    overflow: 'hidden',
                                                    cursor: 'pointer',
                                                    '&:hover .edit-overlay': {
                                                        opacity: 1
                                                    }
                                                }}
                                                onClick={handleProfilePictureClick}
                                                onMouseEnter={() => setProfilePictureHovered(true)}
                                                onMouseLeave={() => setProfilePictureHovered(false)}
                                            >
                                                {profilePictureBlobUrl ? (
                                                    <Box
                                                        component="img"
                                                        src={profilePictureBlobUrl}
                                                        alt={`${memberProfile.firstName} ${memberProfile.lastName}`}
                                                        sx={{
                                                            width: '100%',
                                                            height: '100%',
                                                            objectFit: 'cover'
                                                        }}
                                                    />
                                                ) : (
                                                    <PersonIcon sx={{ fontSize: 40, color: 'grey.500' }} />
                                                )}

                                                {/* Edit Overlay */}
                                                <Box
                                                    className="edit-overlay"
                                                    sx={{
                                                        position: 'absolute',
                                                        top: 0,
                                                        left: 0,
                                                        right: 0,
                                                        bottom: 0,
                                                        backgroundColor: 'rgba(0, 0, 0, 0.6)',
                                                        display: 'flex',
                                                        alignItems: 'center',
                                                        justifyContent: 'center',
                                                        opacity: 0,
                                                        transition: 'opacity 0.3s',
                                                    }}
                                                >
                                                    {uploadingPicture ? (
                                                        <CircularProgress size={24} sx={{ color: 'white' }} />
                                                    ) : (
                                                        <PhotoCameraIcon sx={{ fontSize: 32, color: 'white' }} />
                                                    )}
                                                </Box>
                                            </Box>

                                            {/* Hidden file input */}
                                            <input
                                                ref={fileInputRef}
                                                type="file"
                                                accept="image/jpeg,image/jpg,image/png"
                                                style={{ display: 'none' }}
                                                onChange={handleFileSelect}
                                            />

                                            <Typography variant="caption" display="block" textAlign="center" sx={{ mt: 1, color: 'grey.600' }}>
                                                Profile Picture
                                            </Typography>
                                        </Grid>

                                        {/* Personal Information */}
                                        <Grid size={{ xs: 12, sm: 9, md: 10 }}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                                <PersonIcon sx={{ mr: 1 }} />
                                                <Typography variant="h6">Personal Information</Typography>

                                            </Box>
                                            <Grid container spacing={2}>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Full Name</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {`${memberProfile.firstName} ${memberProfile.lastName}`}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Email</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {memberProfile.email}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Phone</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {memberProfile.phone || 'Not provided'}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Mobile</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {memberProfile.mobile || 'Not provided'}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Date of Birth</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {formatDate(memberProfile.dateOfBirth || '')}
                                                    </Typography>
                                                </Grid>
                                                <Grid size={{ xs: 12, sm: 6, md: 4 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Email Updates</Typography>
                                                    <Box>
                                                        <Chip
                                                            label={memberProfile.emailUpdates !== false ? 'Subscribed' : 'Unsubscribed'}
                                                            size="small"
                                                            color={memberProfile.emailUpdates !== false ? 'success' : 'default'}
                                                        />
                                                    </Box>
                                                </Grid>
                                                <Grid size={{ xs: 12 }}>
                                                    <Typography variant="subtitle2" color="text.secondary">Address</Typography>
                                                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                        {[memberProfile.address, memberProfile.city, memberProfile.state, memberProfile.zipCode, memberProfile.country]
                                                            .filter(Boolean).join(', ') || 'Not provided'}
                                                    </Typography>
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        {/* Second Row: Membership, Fees, and Discounts */}
                        <Grid size={{ xs: 12, md: 6 }}>
                            {/* Pending Scheduled Change Alert */}
                            {memberProfile.pendingScheduledChangeId && pendingScheduledChange?.status === 'pending' && (
                                <Alert
                                    severity="info"
                                    sx={{ mb: 2 }}
                                    action={
                                        <Button
                                            color="inherit"
                                            size="small"
                                            onClick={handleViewScheduledChange}
                                        >
                                            View/Cancel
                                        </Button>
                                    }
                                >
                                    <Typography variant="body2" fontWeight="bold">
                                        Pending Membership Change
                                    </Typography>
                                    <Typography variant="body2">
                                        Click "View/Cancel" to see details or cancel it.
                                    </Typography>
                                </Alert>
                            )}
                            
                            <Card>
                                <CardHeader
                                    title="Membership Information"
                                    avatar={<CardMembershipIcon />}
                                />
                                <CardContent>
                                    <Grid container spacing={2}>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Membership Number</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {memberProfile.membershipNumber || 'Not assigned'}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Membership Tier</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {memberProfile.membershipTier?.name || 'Not assigned'}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                            <Box>
                                                <Chip
                                                    label={memberProfile.status?.replace('_', ' ') || 'Unknown'}
                                                    size="small"
                                                    color={getStatusColor(memberProfile.status) as any}
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Monthly Price</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {formatCurrency(memberProfile.monthlyPrice)}
                                            </Typography>
                                        </Grid>

                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Group Role</Typography>
                                            <Box>
                                                <Chip
                                                    label={memberProfile.groupRole || 'primary'}
                                                    size="small"
                                                    color="primary"
                                                />
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Member of Group</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {memberGroup?.groupName || 'No group assigned'}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Start Date</Typography>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                    {formatDate(memberProfile.startDate)}
                                                </Typography>
                                            </Box>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">End Date</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {formatDate(memberProfile.endDate)}
                                            </Typography>
                                        </Grid>
                                        <Grid size={{ xs: 12, sm: 6 }}>
                                            <Typography variant="subtitle2" color="text.secondary">Auto Renew</Typography>
                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                {memberProfile.autoRenew ? 'Yes' : 'No'}
                                            </Typography>
                                        </Grid>


                                        {memberProfile.notes && (
                                            <Grid size={{ xs: 12 }}>
                                                <Typography variant="subtitle2" color="text.secondary">Notes</Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                    {memberProfile.notes}
                                                </Typography>
                                            </Grid>
                                        )}
                                    </Grid>
                                </CardContent>
                            </Card>
                        </Grid>

                        <Grid size={{ xs: 12, md: 6 }}>
                            <Grid container spacing={3}>
                                {/* Additional Fees */}
                                <Grid size={{ xs: 12 }}>
                                    <Card>
                                        <CardHeader
                                            title="Additional Fees"
                                            subheader="One-time fees assigned to this member"
                                        />
                                        <CardContent sx={{ pt: 1 }}>
                                            {selectedFees.length > 0 ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {selectedFees.filter(fee => fee != null).map((fee) => (
                                                        <Box key={fee.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                {fee.feeName}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {`${fee.feeType} - ${formatCurrency(fee.amount)}`}
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}>
                                                    No additional fees assigned
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Discounts */}
                                <Grid size={{ xs: 12 }}>
                                    <Card>
                                        <CardHeader
                                            title="Discounts"
                                            subheader="Discounts applied to this member"
                                        />
                                        <CardContent sx={{ pt: 1 }}>
                                            {selectedDiscounts.length > 0 ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {selectedDiscounts.filter(discount => discount != null).map((discount) => (
                                                        <Box key={discount.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                                            <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                {discount.name}
                                                            </Typography>
                                                            <Typography variant="body2" color="text.secondary">
                                                                {discount.category === 'percentage'
                                                                    ? `${discount.value}% discount (${discount.type})`
                                                                    : `${formatCurrency(discount.value)} off (${discount.type})`
                                                                }
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}>
                                                    No discounts applied
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>

                                {/* Add-Ons */}
                                <Grid size={{ xs: 12 }}>
                                    <Card>
                                        <CardHeader
                                            title="Add-Ons"
                                            subheader="Membership add-ons subscribed by this member"
                                        />
                                        <CardContent sx={{ pt: 1 }}>
                                            {memberAddOns.filter(addOn => addOn.status === 'active').length > 0 ? (
                                                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                                    {memberAddOns.filter(addOn => addOn.status === 'active').map((memberAddOn) => (
                                                        <Box key={memberAddOn.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 0.5 }}>
                                                            <Box>
                                                                <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                                                    {memberAddOn.addOn?.name || 'Unknown Add-On'}
                                                                </Typography>
                                                                {memberAddOn.scheduledRemovalDate && (
                                                                    <Typography variant="caption" color="warning.main">
                                                                        Scheduled for removal: {formatDate(memberAddOn.scheduledRemovalDate)}
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                            <Typography variant="body2" color="text.secondary">
                                                                ${memberAddOn.monthlyFee}/mo
                                                            </Typography>
                                                        </Box>
                                                    ))}
                                                </Box>
                                            ) : (
                                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic', textAlign: 'center', py: 1 }}>
                                                    No add-ons subscribed
                                                </Typography>
                                            )}
                                        </CardContent>
                                    </Card>
                                </Grid>
                            </Grid>
                        </Grid>
                    </Grid>
                </TabPanel>

                {/* History Tab */}
                <TabPanel value={currentTab} index={1}>
                    <Card>
                        <CardHeader
                            title="Account History"
                            subheader="All changes made to this member profile"
                            avatar={<HistoryIcon />}
                        />
                        <CardContent>
                            {auditLogsLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : auditLogsError ? (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    {auditLogsError}
                                </Alert>
                            ) : auditLogs.length === 0 ? (
                                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', p: 4 }}>
                                    No history records found for this member profile.
                                </Typography>
                            ) : (
                                <>
                                    <TableContainer component={Paper} variant="outlined">
                                        <Table>
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Date & Time</TableCell>
                                                    <TableCell>Action</TableCell>
                                                    <TableCell>Changes</TableCell>
                                                    <TableCell>User</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {auditLogs.map((log) => (
                                                    <TableRow key={log.id}>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {formatDate(log.createdAt)}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {new Date(log.createdAt).toLocaleTimeString()}
                                                            </Typography>
                                                        </TableCell>
                                                        <TableCell>
                                                            <Chip
                                                                label={log.action.replace('_', ' ').toUpperCase()}
                                                                size="small"
                                                                color={getActionColor(log.action) as any}
                                                            />
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {getChangedFieldsDisplay(log)}
                                                            </Typography>
                                                            {log.description && (
                                                                <Typography variant="caption" color="text.secondary" display="block">
                                                                    {log.description}
                                                                </Typography>
                                                            )}
                                                        </TableCell>
                                                        <TableCell>
                                                            <Typography variant="body2">
                                                                {log.userFullName || 'Unknown'}
                                                            </Typography>
                                                            <Typography variant="caption" color="text.secondary">
                                                                {log.userEmail}
                                                            </Typography>
                                                        </TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    </TableContainer>

                                    {auditLogsPagination.pages > 1 && (
                                        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
                                            <Pagination
                                                count={auditLogsPagination.pages}
                                                page={auditLogsPagination.page}
                                                onChange={handleAuditLogPageChange}
                                                color="primary"
                                            />
                                        </Box>
                                    )}
                                </>
                            )}
                        </CardContent>
                    </Card>
                </TabPanel>

                {/* Transactions Tab */}
                <TabPanel value={currentTab} index={2}>
                    {/* Group Billing Notice - All Members */}
                    {memberGroup && memberGroup.chargeToGroupPrimaryContact && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                This member is part of group <strong>{memberGroup.groupName}</strong>. 
                                All charges are billed to the group's primary contact.
                            </Typography>
                        </Alert>
                    )}

                    <Card>
                        <CardContent>
                            <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                                <Box>
                                    <Typography variant="h6">
                                        Billing & Transaction History
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary">
                                        All transactions being billed to this member
                                    </Typography>
                                </Box>
                                <Box display="flex" gap={1}>
                                    {retryFailedTransactionsCount > 0 && (currentUser?.role === 'super_admin' || currentUser?.role === 'admin') && (
                                        <Button
                                            variant="contained"
                                            color="primary"
                                            onClick={() => setRetryPaymentDialogOpen(true)}
                                            disabled={retryPaymentProcessing}
                                            startIcon={retryPaymentProcessing ? <CircularProgress size={20} /> : undefined}
                                        >
                                            Retry All Failed Payments ({retryFailedTransactionsCount})
                                        </Button>
                                    )}
                                    <Button
                                        variant="outlined"
                                        startIcon={<DownloadIcon />}
                                        onClick={handleExportTransactions}
                                        disabled={transactions.length === 0}
                                    >
                                        Export
                                    </Button>
                                </Box>
                            </Box>

                            {transactionsLoading ? (
                                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                                    <CircularProgress />
                                </Box>
                            ) : transactionsError ? (
                                <Alert severity="warning" sx={{ mb: 2 }}>
                                    {transactionsError}
                                </Alert>
                            ) : (
                                <Box sx={{ height: 600, width: '100%' }}>
                                    <DataGrid
                                        rows={transactions}
                                        columns={[
                                            {
                                                field: 'transactionDate',
                                                headerName: 'Transaction Date',
                                                width: 150,
                                                valueGetter: (value, row) => value || row.createdAt,
                                                renderCell: (params: GridRenderCellParams) => {
                                                    return formatDate(params.value || params.row.createdAt);
                                                },
                                            },
                                            {
                                                field: 'memberProfile',
                                                headerName: 'Member Charged',
                                                width: 180,
                                                valueGetter: (value: any) => {
                                                    if (!value) return 'N/A';
                                                    return `${value.firstName || ''} ${value.lastName || ''}`;
                                                },
                                            },
                                            {
                                                field: 'description',
                                                headerName: 'Description',
                                                flex: 1,
                                                minWidth: 180,
                                                valueGetter: (value) => value || '-',
                                            },
                                            {
                                                field: 'type',
                                                headerName: 'Type',
                                                width: 160,
                                                renderCell: (params: GridRenderCellParams) => (
                                                    <Chip
                                                        label={params.value.split('_').map((word: string) =>
                                                            word.charAt(0).toUpperCase() + word.slice(1)
                                                        ).join(' ')}
                                                        size="small"
                                                        color={getTransactionTypeColor(params.value) as any}
                                                    />
                                                ),
                                            },
                                            {
                                                field: 'totalAmount',
                                                headerName: 'Amount',
                                                width: 110,
                                                type: 'number',
                                                valueGetter: (value, row) => value || row.amount,
                                                renderCell: (params: GridRenderCellParams) => `$${params.value}`,
                                            },
                                            {
                                                field: 'status',
                                                headerName: 'Status',
                                                width: 120,
                                                renderCell: (params: GridRenderCellParams) => (
                                                    <Chip
                                                        label={params.value}
                                                        size="small"
                                                        color={getTransactionStatusColor(params.value) as any}
                                                    />
                                                ),
                                            },
                                            {
                                                field: 'actions',
                                                headerName: '',
                                                width: 100,
                                                sortable: false,
                                                filterable: false,
                                                renderCell: (params: GridRenderCellParams) => (
                                                    params.row.status === 'completed' && params.row.paymentGroupId ? (
                                                        <IconButton
                                                            size="small"
                                                            onClick={(e) => handleDownloadInvoice(params.row, e)}
                                                            title="Download Invoice"
                                                            sx={{
                                                                color: 'primary.main',
                                                                '&:hover': {
                                                                    backgroundColor: 'action.hover'
                                                                }
                                                            }}
                                                        >
                                                            <DownloadIcon fontSize="small" />
                                                        </IconButton>
                                                    ) : null
                                                ),
                                            },
                                        ]}
                                        initialState={{
                                            pagination: {
                                                paginationModel: { pageSize: 10 },
                                            },
                                            sorting: {
                                                sortModel: [{ field: 'transactionDate', sort: 'desc' }],
                                            },
                                        }}
                                        pageSizeOptions={[10, 25, 50, 100]}
                                        disableRowSelectionOnClick
                                        onRowClick={(params) => handleTransactionClick(params.row)}
                                        sx={{
                                            '& .MuiDataGrid-row': {
                                                cursor: 'pointer',
                                            },
                                        }}
                                    />
                                </Box>
                            )}
                        </CardContent>
                    </Card>
                </TabPanel>

                {/* Billing Tab */}
                <TabPanel value={currentTab} index={3}>
                    {/* Group Billing Notice - All Members */}
                    {memberGroup && memberGroup.chargeToGroupPrimaryContact && (
                        <Alert severity="info" sx={{ mb: 3 }}>
                            <Typography variant="body2">
                                This member is part of group <strong>{memberGroup.groupName}</strong>. 
                                All charges are billed to the group's primary contact.
                            </Typography>
                        </Alert>
                    )}

                    {paymentMethodsLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : paymentMethodsError ? (
                        <Alert severity="error">{paymentMethodsError}</Alert>
                    ) : (
                        <Box>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
                                        <Box display="flex" alignItems="center">
                                            <CreditCardIcon sx={{ mr: 1, color: 'primary.main' }} />
                                            <Typography variant="h6">Payment Method</Typography>
                                        </Box>
                                        <Button
                                            variant="contained"
                                            startIcon={<EditIcon />}
                                            onClick={handleUpdateCardOnFile}
                                            disabled={isSubmitting || !helcimScriptLoaded}
                                        >
                                            {!helcimScriptLoaded ? 'Loading...' :
                                                isSubmitting ? 'Processing...' : 'Update Card on File'}
                                        </Button>
                                    </Box>

                                    {paymentMethods.length > 0 ? (
                                        <Box>
                                            {paymentMethods.map((method) => {
                                                const expired = isCardExpired(method.expiryMonth, method.expiryYear);
                                                return (
                                                    <Paper
                                                        key={method.id}
                                                        elevation={2}
                                                        sx={{
                                                            p: 2.5,
                                                            mb: 2,
                                                            background: expired
                                                                ? 'linear-gradient(135deg, #757575 0%, #616161 100%)'
                                                                : 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                                                            color: 'white',
                                                            borderRadius: 2,
                                                            position: 'relative'
                                                        }}
                                                    >
                                                        <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                                                            <Box flex={1}>
                                                                {method.cardHolderName && (
                                                                    <Typography variant="subtitle1" sx={{ fontWeight: 600, mb: 1 }}>
                                                                        {method.cardHolderName}
                                                                    </Typography>
                                                                )}
                                                                <Typography variant="body2" sx={{ opacity: 0.9, mb: 0.5 }}>
                                                                    {method.brand} {method.type.replace('_', ' ')}
                                                                </Typography>
                                                                <Typography variant="h6" sx={{ letterSpacing: 2, mb: 1 }}>
                                                                    •••• •••• •••• {method.lastFour}
                                                                </Typography>
                                                                <Box display="flex" gap={3}>
                                                                    <Box>
                                                                        <Typography variant="caption" sx={{ opacity: 0.7, display: 'block' }}>
                                                                            Expiry
                                                                        </Typography>
                                                                        <Typography variant="body2" sx={{ opacity: 0.9 }}>
                                                                            {String(method.expiryMonth).padStart(2, '0')}/{method.expiryYear}
                                                                        </Typography>
                                                                    </Box>
                                                                </Box>
                                                                {expired && (
                                                                    <Alert severity="warning" sx={{ mt: 2, py: 0 }}>
                                                                        This card has expired
                                                                    </Alert>
                                                                )}
                                                            </Box>
                                                            <Box textAlign="right">
                                                                <CreditCardIcon sx={{ fontSize: 40, opacity: 0.8 }} />
                                                                {method.isDefault && (
                                                                    <Typography variant="caption" display="block" sx={{ opacity: 0.8, mt: 0.5 }}>
                                                                        Default
                                                                    </Typography>
                                                                )}
                                                            </Box>
                                                        </Box>
                                                        <Box display="flex" gap={1} mt={2}>
                                                            {!method.isDefault && (
                                                                <Button
                                                                    size="small"
                                                                    variant="outlined"
                                                                    startIcon={expired ? <StarBorderIcon /> : <StarIcon />}
                                                                    onClick={() => handleSetDefaultCard(method.id)}
                                                                    disabled={expired}
                                                                    sx={{
                                                                        color: 'white',
                                                                        borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                        '&:hover': {
                                                                            borderColor: 'white',
                                                                            backgroundColor: 'rgba(255, 255, 255, 0.1)'
                                                                        },
                                                                        '&:disabled': {
                                                                            color: 'rgba(255, 255, 255, 0.3)',
                                                                            borderColor: 'rgba(255, 255, 255, 0.2)'
                                                                        }
                                                                    }}
                                                                >
                                                                    Set as Default
                                                                </Button>
                                                            )}
                                                            <Button
                                                                size="small"
                                                                variant="outlined"
                                                                color="error"
                                                                startIcon={<DeleteIcon />}
                                                                onClick={() => openDeleteDialog(method.id)}
                                                                sx={{
                                                                    color: 'white',
                                                                    borderColor: 'rgba(255, 255, 255, 0.5)',
                                                                    '&:hover': {
                                                                        borderColor: '#ff1744',
                                                                        backgroundColor: 'rgba(255, 23, 68, 0.1)'
                                                                    }
                                                                }}
                                                            >
                                                                Delete
                                                            </Button>
                                                        </Box>
                                                    </Paper>
                                                );
                                            })}
                                        </Box>
                                    ) : (
                                        <Box textAlign="center" py={3}>
                                            <CreditCardIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
                                            <Typography variant="body1" color="text.secondary" gutterBottom>
                                                No payment method on file
                                            </Typography>
                                            <Typography variant="body2" color="text.secondary">
                                                Add a payment method using the button above
                                            </Typography>
                                        </Box>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                </TabPanel>

                {/* Onboarding Tab */}
                <TabPanel value={currentTab} index={4}>
                    {onboardingLoading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                            <CircularProgress />
                        </Box>
                    ) : onboardingError ? (
                        <Alert severity="error">{onboardingError}</Alert>
                    ) : (
                        <Box>
                            <Card>
                                <CardContent>
                                    <Box display="flex" alignItems="center" mb={2}>
                                        <AssignmentIcon sx={{ mr: 1, color: 'primary.main' }} />
                                        <Typography variant="h6">Onboarding Status</Typography>
                                    </Box>

                                    {onboardingStatus ? (
                                        <Box>
                                            <Box mb={3}>
                                                <Typography variant="body1" gutterBottom>
                                                    <strong>Completion Status:</strong> {onboardingStatus.isComplete ? (
                                                        <Chip label="Complete" color="success" size="small" icon={<CheckCircleIcon />} sx={{ ml: 1 }} />
                                                    ) : (
                                                        <Chip label="Incomplete" color="warning" size="small" sx={{ ml: 1 }} />
                                                    )}
                                                </Typography>
                                                {onboardingStatus.completedAt && (
                                                    <Typography variant="body2" color="text.secondary">
                                                        Completed on: {formatDate(onboardingStatus.completedAt)}
                                                    </Typography>
                                                )}
                                            </Box>

                                            {onboardingStatus.documents && onboardingStatus.documents.length > 0 ? (
                                                <>
                                                    <Typography variant="subtitle1" gutterBottom sx={{ mt: 3 }}>
                                                        Required Documents
                                                    </Typography>
                                                    <TableContainer component={Paper} variant="outlined">
                                                        <Table>
                                                            <TableHead>
                                                                <TableRow>
                                                                    <TableCell>Document Name</TableCell>
                                                                    <TableCell>Status</TableCell>
                                                                    <TableCell>Accepted Date</TableCell>
                                                                </TableRow>
                                                            </TableHead>
                                                            <TableBody>
                                                                {onboardingStatus.documents.map((doc: any) => (
                                                                    <TableRow key={doc.id || doc.documentName}>
                                                                        <TableCell>{doc.documentName}</TableCell>
                                                                        <TableCell>
                                                                            {doc.accepted ? (
                                                                                <Chip
                                                                                    label="Accepted"
                                                                                    color="success"
                                                                                    size="small"
                                                                                    icon={<CheckCircleIcon />}
                                                                                />
                                                                            ) : (
                                                                                <Chip
                                                                                    label="Pending"
                                                                                    color="warning"
                                                                                    size="small"
                                                                                />
                                                                            )}
                                                                        </TableCell>
                                                                        <TableCell>
                                                                            {doc.acceptedAt ? formatDate(doc.acceptedAt) : '-'}
                                                                        </TableCell>
                                                                    </TableRow>
                                                                ))}
                                                            </TableBody>
                                                        </Table>
                                                    </TableContainer>
                                                </>
                                            ) : (
                                                <Alert severity="info">No onboarding documents configured for this member.</Alert>
                                            )}
                                        </Box>
                                    ) : (
                                        <Alert severity="info">No onboarding information available for this member.</Alert>
                                    )}
                                </CardContent>
                            </Card>
                        </Box>
                    )}
                </TabPanel>

                {/* PT Packs Tab */}
                <TabPanel value={currentTab} index={5}>
                    {memberProfile && (
                        <PTPackManager
                            memberProfileId={memberProfile.id}
                            memberName={`${memberProfile.firstName} ${memberProfile.lastName}`}
                        />
                    )}
                </TabPanel>
            </Box>

            {/* HelcimPay.js Payment Form Container */}
            <div id="helcimpay-container">
                {/* The HelcimPay.js iframe will be rendered here */}
            </div>

            {/* Edit Member Profile Dialog */}
            <Dialog
                open={editDialogOpen}
                disableEscapeKeyDown={true}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>Edit Member Profile</DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        {/* Personal Information Section */}
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 2, mb: 1 }}>
                                Personal Information
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="First Name"
                                value={editForm.firstName}
                                onChange={(e) => handleEditChange('firstName', e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Last Name"
                                value={editForm.lastName}
                                onChange={(e) => handleEditChange('lastName', e.target.value)}
                                fullWidth
                                required
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Email"
                                type="email"
                                value={editForm.email}
                                onChange={(e) => handleEditChange('email', e.target.value)}
                                fullWidth
                                required
                                disabled
                                helperText="Email cannot be changed"
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={editForm.emailUpdates}
                                        onChange={(e) => handleEditChange('emailUpdates', e.target.checked)}
                                        color="primary"
                                    />
                                }
                                label="Subscribe to Email Updates"
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Phone"
                                value={editForm.phone}
                                onChange={(e) => handleEditChange('phone', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Mobile"
                                value={editForm.mobile}
                                onChange={(e) => handleEditChange('mobile', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Address"
                                value={editForm.address}
                                onChange={(e) => handleEditChange('address', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="City"
                                value={editForm.city}
                                onChange={(e) => handleEditChange('city', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="State/Province"
                                value={editForm.state}
                                onChange={(e) => handleEditChange('state', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="ZIP/Postal Code"
                                value={editForm.zipCode}
                                onChange={(e) => handleEditChange('zipCode', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Country"
                                value={editForm.country}
                                onChange={(e) => handleEditChange('country', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Date of Birth"
                                type="date"
                                value={editForm.dateOfBirth ? editForm.dateOfBirth.split('T')[0] : ''}
                                onChange={(e) => handleEditChange('dateOfBirth', e.target.value)}
                                fullWidth
                                InputLabelProps={{
                                    shrink: true,
                                }}
                            />
                        </Grid>

                        {/* Membership Information Section */}
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
                                Membership Information
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Membership Tier</InputLabel>
                                <Select
                                    value={editForm.membershipTierId}
                                    onChange={(e) => handleEditChange('membershipTierId', e.target.value)}
                                    label="Membership Tier"
                                >
                                    <MenuItem value="">
                                        <em>No Tier Selected</em>
                                    </MenuItem>
                                    {membershipTiers.map((tier) => (
                                        <MenuItem key={tier.id} value={tier.id}>
                                            {tier.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Status</InputLabel>
                                <Select
                                    value={editForm.status}
                                    onChange={(e) => handleEditChange('status', e.target.value)}
                                    label="Status"
                                >
                                    <MenuItem value="active">Active</MenuItem>
                                    <MenuItem value="pending">Pending</MenuItem>
                                    <MenuItem value="cancelled">Cancelled</MenuItem>
                                    <MenuItem value="on_hold">On Hold</MenuItem>
                                    <MenuItem value="suspended" disabled>Suspended (System Only)</MenuItem>
                                    <MenuItem value="expired" disabled>Expired (System Only)</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Start Date"
                                type="date"
                                value={editForm.startDate ? editForm.startDate.split('T')[0] : ''}
                                onChange={(e) => handleEditChange('startDate', e.target.value)}
                                fullWidth
                                disabled={isStartDateReadOnly()}
                                InputLabelProps={{
                                    shrink: true,
                                }}
                                helperText={
                                    isStartDateReadOnly()
                                        ? "Start date cannot be changed once it has passed"
                                        : "Changes to the start date may affect membership status"
                                }
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Monthly Price"
                                type="number"
                                value={editForm.monthlyPrice}
                                onChange={(e) => handleEditChange('monthlyPrice', e.target.value)}
                                fullWidth
                                InputProps={{
                                    startAdornment: '$',
                                }}
                            />
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <FormControl fullWidth>
                                <InputLabel>Auto Renew</InputLabel>
                                <Select
                                    value={editForm.autoRenew ? 'true' : 'false'}
                                    onChange={(e) => handleEditChange('autoRenew', e.target.value === 'true')}
                                    label="Auto Renew"
                                >
                                    <MenuItem value="true">Yes</MenuItem>
                                    <MenuItem value="false">No</MenuItem>
                                </Select>
                            </FormControl>
                        </Grid>
                        <Grid size={{ xs: 12, sm: 6 }}>
                            <TextField
                                label="Membership Number"
                                value={editForm.membershipNumber}
                                onChange={(e) => handleEditChange('membershipNumber', e.target.value)}
                                fullWidth
                            />
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <TextField
                                label="Notes"
                                value={editForm.notes}
                                onChange={(e) => handleEditChange('notes', e.target.value)}
                                fullWidth
                                multiline
                                rows={3}
                            />
                        </Grid>

                        {/* Additional Fees Section */}
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
                                Additional Fees
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Manage one-time fees for this member
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleFeeDialogToggle}
                                    size="small"
                                >
                                    Add Fee
                                </Button>
                            </Box>
                            {selectedFees.length > 0 ? (
                                <List dense>
                                    {selectedFees.filter(fee => fee != null).map((fee) => (
                                        <ListItem
                                            key={fee.id}
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    aria-label="delete"
                                                    onClick={() => handleRemoveFee(fee.id)}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemText
                                                primary={fee.feeName}
                                                secondary={`${fee.feeType} - ${formatCurrency(fee.amount)}`}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No additional fees assigned
                                </Typography>
                            )}
                        </Grid>

                        {/* Discounts Section */}
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
                                Discounts
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Manage discounts for this member
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleDiscountDialogToggle}
                                    size="small"
                                >
                                    Add Discount
                                </Button>
                            </Box>
                            {selectedDiscounts.length > 0 ? (
                                <List dense>
                                    {selectedDiscounts.filter(discount => discount != null).map((discount) => (
                                        <ListItem
                                            key={discount.id}
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    aria-label="delete"
                                                    onClick={() => handleRemoveDiscount(discount.id)}
                                                    size="small"
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemText
                                                primary={discount.name}
                                                secondary={
                                                    discount.category === 'percentage'
                                                        ? `${discount.value}% (${discount.type})`
                                                        : `${formatCurrency(discount.value)} (${discount.type})`
                                                }
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No discounts assigned
                                </Typography>
                            )}
                        </Grid>

                        {/* Add-Ons Section */}
                        <Grid size={{ xs: 12 }}>
                            <Typography variant="h6" gutterBottom sx={{ mt: 3, mb: 1 }}>
                                Add-Ons
                            </Typography>
                        </Grid>
                        <Grid size={{ xs: 12 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                                <Typography variant="body2" color="text.secondary">
                                    Manage membership add-ons for this member
                                </Typography>
                                <Button
                                    variant="outlined"
                                    color="primary"
                                    startIcon={<AddIcon />}
                                    onClick={handleAddOnDialogToggle}
                                    size="small"
                                    disabled={addOnLoading}
                                >
                                    Add Add-On
                                </Button>
                            </Box>
                            {memberAddOns.filter(addOn => addOn.status === 'active').length > 0 ? (
                                <List dense>
                                    {memberAddOns.filter(addOn => addOn.status === 'active').map((memberAddOn) => (
                                        <ListItem
                                            key={memberAddOn.id}
                                            secondaryAction={
                                                <IconButton
                                                    edge="end"
                                                    aria-label="delete"
                                                    onClick={() => handleRemoveAddOn(memberAddOn.id, memberAddOn.addOnId)}
                                                    size="small"
                                                    disabled={addOnLoading || memberAddOn.scheduledRemovalDate !== null}
                                                >
                                                    <DeleteIcon />
                                                </IconButton>
                                            }
                                        >
                                            <ListItemText
                                                primary={memberAddOn.addOn?.name || 'Unknown Add-On'}
                                                secondary={
                                                    <>
                                                        ${memberAddOn.monthlyFee}/mo
                                                        {memberAddOn.scheduledRemovalDate && (
                                                            <Typography component="span" variant="caption" color="warning.main" sx={{ ml: 1 }}>
                                                                (Scheduled for removal: {formatDate(memberAddOn.scheduledRemovalDate)})
                                                            </Typography>
                                                        )}
                                                    </>
                                                }
                                                secondaryTypographyProps={{ component: 'div' }}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            ) : (
                                <Typography variant="body2" color="text.secondary" sx={{ fontStyle: 'italic' }}>
                                    No add-ons subscribed
                                </Typography>
                            )}
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleEditClose}>Cancel</Button>
                    <Button onClick={handleEditSubmit} variant="contained" color="primary">
                        Update Member Profile
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Upgrade Confirmation Dialog */}
            <Dialog
                open={upgradeConfirmOpen}
                onClose={() => setUpgradeConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Membership Upgrade</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            <strong>Immediate Upgrade:</strong> This membership tier change will take effect immediately.
                        </Typography>
                        {upgradeTierInfo && (
                            <>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Your membership will change from <strong>{upgradeTierInfo.currentTierName}</strong> (${upgradeTierInfo.currentPrice.toFixed(2)}/month) to <strong>{upgradeTierInfo.newTierName}</strong> (${upgradeTierInfo.newPrice.toFixed(2)}/month).
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    <strong>Prorated Charge:</strong> ${upgradeTierInfo.proratedCharge.toFixed(2)}
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1, fontSize: '0.875rem', fontStyle: 'italic' }}>
                                    This charge represents the difference in tier price for the remaining days in the current billing period.
                                </Typography>
                            </>
                        )}
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        Click "Confirm Upgrade" to process this change and create the prorated charge, or "Cancel" to keep your current membership tier.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUpgradeConfirmOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={async () => {
                            setUpgradeConfirmOpen(false);
                            await executeEdit();
                        }} 
                        variant="contained" 
                        color="primary"
                    >
                        Confirm Upgrade
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Downgrade Confirmation Dialog */}
            <Dialog
                open={downgradeConfirmOpen}
                onClose={() => setDowngradeConfirmOpen(false)}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Membership Downgrade</DialogTitle>
                <DialogContent>
                    <Alert severity="warning" sx={{ mb: 2 }}>
                        <Typography variant="body2" gutterBottom>
                            <strong>Scheduled Downgrade:</strong> This membership tier change will not take effect immediately.
                        </Typography>
                        {downgradeTierInfo && (
                            <>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    Your membership will change from <strong>{downgradeTierInfo.currentTierName}</strong> (${downgradeTierInfo.currentPrice.toFixed(2)}/month) to <strong>{downgradeTierInfo.newTierName}</strong> (${downgradeTierInfo.newPrice.toFixed(2)}/month) on <strong>{downgradeTierInfo.effectiveDate}</strong>.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    You can continue to enjoy your current membership benefits until the effective date.
                                </Typography>
                            </>
                        )}
                    </Alert>
                    <Typography variant="body2" color="text.secondary">
                        Click "Confirm Downgrade" to schedule this change, or "Cancel" to keep your current membership tier.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDowngradeConfirmOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={async () => {
                            setDowngradeConfirmOpen(false);
                            await executeEdit();
                        }} 
                        variant="contained" 
                        color="warning"
                    >
                        Confirm Downgrade
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Cancellation Confirmation Dialog */}
            <Dialog
                open={cancellationConfirmOpen}
                onClose={handleCancellationCancel}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Confirm Membership Cancellation</DialogTitle>
                <DialogContent>
                    {checkingCancellation ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : cancellationType === 'scheduled' ? (
                        <>
                            <Alert severity="warning" sx={{ mb: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                    <strong>Scheduled Cancellation:</strong> This member has already paid for the current month.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    • The cancellation will be scheduled for the <strong>last day of the month</strong>.
                                </Typography>
                                <Typography variant="body2">
                                    • Member will remain <strong>Active</strong> until the scheduled date
                                </Typography>
                                <Typography variant="body2">
                                    • Any pending payments <strong>after</strong> the scheduled date will be cancelled
                                </Typography>
                            </Alert>
                            <Typography variant="body2">
                                Are you sure you want to schedule this cancellation?
                            </Typography>
                        </>
                    ) : cancellationType === 'immediate' ? (
                        <>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                <Typography variant="body2" gutterBottom>
                                    <strong>Immediate Cancellation:</strong> This member has no completed payment for the current month.
                                </Typography>
                                <Typography variant="body2" sx={{ mt: 1 }}>
                                    The membership will be cancelled <strong>immediately</strong>:
                                </Typography>
                                <Typography variant="body2">
                                    • Member status will be set to <strong>Cancelled</strong> right away
                                </Typography>
                                <Typography variant="body2">
                                    • All pending transactions from today onwards will be cancelled
                                </Typography>
                            </Alert>
                            <Typography variant="body2">
                                Are you sure you want to cancel this membership immediately?
                            </Typography>
                        </>
                    ) : null}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCancellationCancel} color="primary">
                        Cancel
                    </Button>
                    <Button 
                        onClick={handleCancellationConfirm} 
                        variant="contained" 
                        color="warning"
                        disabled={checkingCancellation || !cancellationType}
                    >
                        {cancellationType === 'scheduled' ? 'Schedule Cancellation' : 'Cancel Immediately'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Fee Dialog */}
            <Dialog open={feeDialogOpen} disableEscapeKeyDown={true} maxWidth="sm" fullWidth>
                <DialogTitle>Add One-Time Fee</DialogTitle>
                <DialogContent>
                    <List>
                        {oneTimeFees.filter(fee => !selectedFees.some(selected => selected != null && selected.id === fee.id)).map((fee) => (
                            <ListItem
                                key={fee.id}
                                onClick={() => handleAddFee(fee)}
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                            >
                                <ListItemText
                                    primary={fee.feeName}
                                    secondary={`${fee.feeType} - ${formatCurrency(fee.amount)}`}
                                />
                            </ListItem>
                        ))}
                    </List>
                    {oneTimeFees.filter(fee => !selectedFees.some(selected => selected != null && selected.id === fee.id)).length === 0 && (
                        <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                            No additional fees available
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleFeeDialogToggle} color="primary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Discount Dialog */}
            <Dialog open={discountDialogOpen} disableEscapeKeyDown={true} maxWidth="sm" fullWidth>
                <DialogTitle>Add Discount</DialogTitle>
                <DialogContent>
                    <List>
                        {discounts.filter(discount => !selectedDiscounts.some(selected => selected != null && selected.id === discount.id)).map((discount) => (
                            <ListItem
                                key={discount.id}
                                onClick={() => handleAddDiscount(discount)}
                                sx={{ cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' } }}
                            >
                                <ListItemText
                                    primary={discount.name}
                                    secondary={
                                        discount.category === 'percentage'
                                            ? `${discount.value}% (${discount.type})`
                                            : `${formatCurrency(discount.value)} (${discount.type})`
                                    }
                                />
                            </ListItem>
                        ))}
                    </List>
                    {discounts.filter(discount => !selectedDiscounts.some(selected => selected != null && selected.id === discount.id)).length === 0 && (
                        <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                            No additional discounts available
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleDiscountDialogToggle} color="primary">
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add-On Dialog */}
            <Dialog open={addOnDialogOpen} disableEscapeKeyDown={true} maxWidth="sm" fullWidth>
                <DialogTitle>Subscribe to Add-On</DialogTitle>
                <DialogContent>
                    <Alert severity="info" sx={{ mb: 2, mt: 1 }}>
                        <Typography variant="body2">
                            <strong>Billing Note:</strong> Changes to add-ons will take effect at the start of the next billing cycle. 
                            Any charges already incurred for add-ons will not be reversed or cancelled.
                        </Typography>
                    </Alert>
                    <List>
                        {availableAddOns
                            .filter(addOn => addOn.status === 'active')
                            .filter(addOn => !memberAddOns.some(ma => ma.addOnId === addOn.id && ma.status === 'active'))
                            .map((addOn) => (
                                <ListItem key={addOn.id} disablePadding>
                                    <ListItemButton
                                        onClick={() => !addOnLoading && handleShowPreview(addOn.id)}
                                        disabled={addOnLoading}
                                    >
                                        <ListItemText
                                            primary={addOn.name}
                                            secondary={
                                                <>
                                                    {addOn.description && (
                                                        <Typography variant="body2" component="div" color="text.secondary">
                                                            {addOn.description}
                                                        </Typography>
                                                    )}
                                                    <Typography variant="body2" component="div" color="text.secondary" sx={{ mt: 0.5 }}>
                                                        {addOn.setupFee > 0 && `Setup: ${formatCurrency(addOn.setupFee)} | `}
                                                        Monthly: {formatCurrency(addOn.monthlyFee)}
                                                    </Typography>
                                                </>
                                            }
                                            secondaryTypographyProps={{ component: 'div' }}
                                        />
                                    </ListItemButton>
                                </ListItem>
                            ))}
                    </List>
                    {availableAddOns
                        .filter(addOn => addOn.status === 'active')
                        .filter(addOn => !memberAddOns.some(ma => ma.addOnId === addOn.id && ma.status === 'active'))
                        .length === 0 && (
                            <Typography variant="body2" color="textSecondary" sx={{ p: 2, textAlign: 'center' }}>
                                No available add-ons to subscribe to
                            </Typography>
                        )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleAddOnDialogToggle} color="primary" disabled={addOnLoading}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Add-On Subscription Preview Dialog */}
            <Dialog open={previewDialogOpen} onClose={() => !addOnLoading && setPreviewDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Subscription Preview</DialogTitle>
                <DialogContent>
                    {previewData && (
                        <Box>
                            <Typography variant="h6" gutterBottom>
                                {previewData.addOnName}
                            </Typography>

                            <Box sx={{ mt: 2, mb: 2 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    <strong>Initial Charges (Today):</strong>
                                </Typography>

                                {previewData.setupFee > 0 && (
                                    <>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2">Setup Fee</Typography>
                                            <Typography variant="body2">${previewData.setupFee.toFixed(2)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ pl: 2 }}>+ Tax ({(previewData.taxRate * 100).toFixed(0)}%)</Typography>
                                            <Typography variant="body2">${previewData.setupTax.toFixed(2)}</Typography>
                                        </Box>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                                            <Typography variant="body2" fontWeight="bold">Setup Total</Typography>
                                            <Typography variant="body2" fontWeight="bold">${previewData.setupTotal.toFixed(2)}</Typography>
                                        </Box>
                                    </>
                                )}

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                                    <Typography variant="body2">
                                        Monthly Fee ({previewData.proratedDescription})
                                    </Typography>
                                    <Typography variant="body2">${previewData.proratedMonthlyFee.toFixed(2)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2" sx={{ pl: 2 }}>+ Tax ({(previewData.taxRate * 100).toFixed(0)}%)</Typography>
                                    <Typography variant="body2">${previewData.monthlyTax.toFixed(2)}</Typography>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, borderBottom: '1px solid #e0e0e0', pb: 1 }}>
                                    <Typography variant="body2" fontWeight="bold">Monthly Total</Typography>
                                    <Typography variant="body2" fontWeight="bold">${previewData.monthlyTotal.toFixed(2)}</Typography>
                                </Box>

                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 2, p: 1, bgcolor: 'primary.light', borderRadius: 1 }}>
                                    <Typography variant="body1" fontWeight="bold">
                                        Total Charge Today
                                    </Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        ${previewData.grandTotal.toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>

                            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Typography variant="body2" color="text.secondary" gutterBottom>
                                    <strong>Recurring Monthly Charges:</strong>
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                                    <Typography variant="body2">
                                        ${previewData.recurringMonthlyFee.toFixed(2)} + tax
                                    </Typography>
                                    <Typography variant="body2" fontWeight="bold">
                                        ${previewData.recurringMonthlyTotal.toFixed(2)}/month
                                    </Typography>
                                </Box>
                            </Box>

                            <Alert severity="info" sx={{ mt: 2 }}>
                                Charges will be processed at 11 PM today.
                            </Alert>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setPreviewDialogOpen(false)} disabled={addOnLoading}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirmSubscription}
                        variant="contained"
                        color="primary"
                        disabled={addOnLoading}
                    >
                        {addOnLoading ? 'Processing...' : 'Confirm Subscription'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Delete Payment Method Confirmation Dialog */}
            <Dialog
                open={deleteDialogOpen}
                onClose={() => setDeleteDialogOpen(false)}
                aria-labelledby="delete-dialog-title"
                aria-describedby="delete-dialog-description"
            >
                <DialogTitle id="delete-dialog-title">
                    Delete Payment Method
                </DialogTitle>
                <DialogContent>
                    <DialogContentText id="delete-dialog-description">
                        Are you sure you want to delete this payment method? This action cannot be undone.
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setDeleteDialogOpen(false)} color="primary">
                        Cancel
                    </Button>
                    <Button
                        onClick={() => cardToDelete && handleDeleteCard(cardToDelete)}
                        color="error"
                        variant="contained"
                        autoFocus
                    >
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Transaction Details Dialog */}
            <Dialog
                open={transactionDetailsOpen}
                onClose={handleCloseTransactionDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <Typography variant="h6">Transaction Details</Typography>
                        <IconButton
                            edge="end"
                            color="inherit"
                            onClick={handleCloseTransactionDialog}
                            aria-label="close"
                        >
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    {selectedTransaction && (() => {
                        return (
                        <Box sx={{ pt: 2 }}>
                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Transaction Date
                                </Typography>
                                <Typography variant="body1">
                                    {selectedTransaction.transactionDate
                                        ? formatDate(selectedTransaction.transactionDate)
                                        : formatDate(selectedTransaction.createdAt)
                                    }
                                </Typography>
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5, opacity: 0.7 }}>
                                    Created: {formatDate(selectedTransaction.createdAt)}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Member Charged
                                </Typography>
                                <Typography variant="body1">
                                    {selectedTransaction.memberProfile
                                        ? `${selectedTransaction.memberProfile.firstName} ${selectedTransaction.memberProfile.lastName}`
                                        : 'N/A'
                                    }
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Billing Context
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    {(selectedTransaction as any).billedToGroupId ? (
                                        <Chip
                                            label="Group Billing"
                                            size="small"
                                            color="info"
                                            icon={<PersonIcon />}
                                        />
                                    ) : (
                                        <Chip
                                            label="Individual Billing"
                                            size="small"
                                            color="default"
                                        />
                                    )}
                                </Box>
                                {(selectedTransaction as any).billedToGroupId && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                        This charge is billed to the group primary contact
                                    </Typography>
                                )}
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Description
                                </Typography>
                                <Typography variant="body1">
                                    {selectedTransaction.description || 'No description'}
                                </Typography>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Type
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={selectedTransaction.type.split('_').map((word: string) =>
                                            word.charAt(0).toUpperCase() + word.slice(1)
                                        ).join(' ')}
                                        size="small"
                                        color="default"
                                    />
                                </Box>
                            </Box>

                            <Box sx={{ mb: 2 }}>
                                <Typography variant="caption" color="text.secondary">
                                    Status
                                </Typography>
                                <Box sx={{ mt: 0.5 }}>
                                    <Chip
                                        label={selectedTransaction.status}
                                        size="small"
                                        color={getTransactionStatusColor(selectedTransaction.status) as any}
                                    />
                                </Box>
                            </Box>

                            {(selectedTransaction.status === 'failed' || selectedTransaction.status === 'pending') && selectedTransaction.metadata?.lastError && (() => {
                                return (
                                <Box sx={{ mb: 2 }}>
                                    <Alert 
                                        severity={selectedTransaction.metadata?.isTerminalError ? "error" : "warning"} 
                                        sx={{ mt: 0 }}
                                    >
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                                            <Typography variant="caption" fontWeight="bold">
                                                {selectedTransaction.metadata?.isTerminalError ? 'Terminal Error:' : 'Failure Reason:'}
                                            </Typography>
                                            {selectedTransaction.metadata?.isTerminalError && (
                                                <Chip 
                                                    label="Terminal" 
                                                    size="small" 
                                                    color="error" 
                                                    sx={{ height: 18, fontSize: '0.65rem' }} 
                                                />
                                            )}
                                        </Box>
                                        <Typography variant="body2">
                                            {selectedTransaction.metadata.lastError}
                                        </Typography>
                                        {(selectedTransaction.retryCount ?? 0) > 0 && (
                                            <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                                                Retry Attempts: {selectedTransaction.retryCount}
                                            </Typography>
                                        )}
                                        {selectedTransaction.metadata?.isTerminalError && (
                                            <Typography variant="caption" sx={{ display: 'block', mt: 1, fontStyle: 'italic' }}>
                                                This error is terminal and will not be retried automatically.
                                            </Typography>
                                        )}
                                    </Alert>
                                </Box>
                                );
                            })()}

                            <Box sx={{ mb: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Typography variant="body2">Amount</Typography>
                                    <Typography variant="body2">${Number(selectedTransaction.amount).toFixed(2)}</Typography>
                                </Box>
                                {selectedTransaction.discountAmount && selectedTransaction.discountAmount > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2" color="success.main">
                                            Discount {selectedTransaction.discountPercentage > 0 && `(${selectedTransaction.discountPercentage}%)`}
                                        </Typography>
                                        <Typography variant="body2" color="success.main">
                                            -${Number(selectedTransaction.discountAmount).toFixed(2)}
                                        </Typography>
                                    </Box>
                                )}
                                {selectedTransaction.tax && selectedTransaction.tax > 0 && (
                                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                        <Typography variant="body2">Tax</Typography>
                                        <Typography variant="body2">${Number(selectedTransaction.tax).toFixed(2)}</Typography>
                                    </Box>
                                )}
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', pt: 1, borderTop: '1px solid #e0e0e0' }}>
                                    <Typography variant="body1" fontWeight="bold">Total</Typography>
                                    <Typography variant="body1" fontWeight="bold">
                                        ${Number(selectedTransaction.totalAmount || selectedTransaction.amount).toFixed(2)}
                                    </Typography>
                                </Box>
                            </Box>

                            {selectedTransaction.metadata?.adminCancellation && (
                                <Box sx={{ bgcolor: 'error.light', p: 2, borderRadius: 1, mb: 2 }}>
                                    <Typography variant="subtitle2" color="error.dark">Administrative Cancellation</Typography>
                                    <Typography variant="body2" color="error.dark">
                                        {selectedTransaction.metadata.adminCancellationNote}
                                    </Typography>
                                    <Typography variant="caption" color="error.dark">
                                        Cancelled at: {selectedTransaction.metadata.cancelledAt && formatDate(selectedTransaction.metadata.cancelledAt)}
                                    </Typography>
                                </Box>
                            )}

                            {(selectedTransaction.status === 'refunded' || (selectedTransaction.type === 'refund' && selectedTransaction.status === 'completed')) && (
                                <Box sx={{ bgcolor: 'info.light', p: 2, borderRadius: 1, mb: 2 }}>
                                    <Typography variant="subtitle2" color="info.dark" fontWeight="bold">Refund Information</Typography>
                                    <Typography variant="body2" color="info.dark" sx={{ mt: 1 }}>
                                        <strong>Reason:</strong> {selectedTransaction?.metadata?.adminNote || selectedTransaction?.metadata?.refundNote || 'No reason provided'}
                                    </Typography>
                                    {selectedTransaction?.metadata?.refundedAt && (
                                        <Typography variant="caption" color="info.dark" sx={{ display: 'block', mt: 0.5 }}>
                                            Refunded at: {formatDate(selectedTransaction.metadata.refundedAt)}
                                        </Typography>
                                    )}
                                    {selectedTransaction?.metadata?.refundAmount && (
                                        <Typography variant="caption" color="info.dark" sx={{ display: 'block' }}>
                                            Refund amount: ${Number(selectedTransaction.metadata.refundAmount).toFixed(2)}
                                        </Typography>
                                    )}
                                    {selectedTransaction?.metadata?.refundedBy && (
                                        <Typography variant="caption" color="info.dark" sx={{ display: 'block' }}>
                                            Refunded by: {selectedTransaction.metadata.refundedBy}
                                        </Typography>
                                    )}
                                </Box>
                            )}

                            {/* Show the cancel transaction section only for pending or failed transactions and for admin users */}
                            {(selectedTransaction.status === 'pending' || selectedTransaction.status === 'failed')
                                && (currentUser?.role === 'super_admin' || currentUser?.role === 'admin')
                                && (
                                    <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                            Cancel This Transaction
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            To cancel this transaction, please provide a note explaining the reason.
                                            This action cannot be undone.
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Cancellation Note (Required)"
                                            value={adminNote}
                                            onChange={(e) => setAdminNote(e.target.value)}
                                            placeholder="Enter the reason for cancelling this transaction..."
                                            sx={{ mt: 2 }}
                                            required
                                        />
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={handleOpenCancelConfirm}
                                            disabled={!adminNote.trim() || processingAction}
                                            sx={{ mt: 2 }}
                                            fullWidth
                                        >
                                            {processingAction ? <CircularProgress size={24} color="inherit" /> : 'Cancel This Transaction'}
                                        </Button>
                                    </Box>
                                )}

                            {/* Show the refund transaction section only for completed transactions (excluding refund type) and for admin users */}
                            {selectedTransaction.status === 'completed'
                                && selectedTransaction.type !== 'refund'
                                && (
                                    <Box sx={{ mt: 3, p: 2, bgcolor: 'error.light', borderRadius: 1 }}>
                                        <Typography variant="subtitle2" gutterBottom fontWeight="bold">
                                            Refund This Transaction
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" gutterBottom>
                                            To refund this transaction, please provide a note explaining the reason.
                                            The refund will be processed through the payment processor and cannot be reversed.
                                        </Typography>
                                        <TextField
                                            fullWidth
                                            multiline
                                            rows={3}
                                            label="Refund Note (Required)"
                                            value={adminNote}
                                            onChange={(e) => setAdminNote(e.target.value)}
                                            placeholder="Enter the reason for refunding this transaction..."
                                            sx={{ mt: 2 }}
                                            required
                                        />
                                        <Button
                                            variant="contained"
                                            color="error"
                                            onClick={handleOpenRefundConfirm}
                                            disabled={!adminNote.trim() || processingAction}
                                            sx={{ mt: 2 }}
                                            fullWidth
                                        >
                                            {processingAction ? <CircularProgress size={24} color="inherit" /> : 'Refund This Transaction'}
                                        </Button>
                                    </Box>
                                )}
                        </Box>
                        );
                    })()}
                </DialogContent>
            </Dialog>

            {/* Cancel Confirmation Dialog */}
            <Dialog
                open={cancelConfirmOpen}
                onClose={() => !processingAction && setCancelConfirmOpen(false)}
            >
                <DialogTitle>Confirm Transaction Cancellation</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to cancel this transaction?
                    </Typography>
                    <Typography variant="body2" color="error" fontWeight="bold">
                        This action cannot be undone.
                    </Typography>
                    {adminNote && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">Cancellation Note:</Typography>
                            <Typography variant="body2">{adminNote}</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setCancelConfirmOpen(false)} disabled={processingAction}>
                        No, Keep Transaction
                    </Button>
                    <Button
                        onClick={handleCancelTransaction}
                        color="error"
                        variant="contained"
                        disabled={processingAction}
                    >
                        {processingAction ? 'Cancelling...' : 'Yes, Cancel Transaction'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Refund Confirmation Dialog */}
            <Dialog
                open={refundConfirmOpen}
                onClose={() => !processingAction && setRefundConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>Confirm Transaction Refund</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        Are you sure you want to refund this transaction?
                    </Typography>
                    <Typography variant="body2" color="error" fontWeight="bold" sx={{ mt: 2 }}>
                        ⚠️ Warning: This will process a refund to the member through the payment processor. This action cannot be reversed.
                    </Typography>
                    {selectedTransaction && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="body2">
                                <strong>Amount to refund:</strong> ${Number(selectedTransaction.totalAmount || selectedTransaction.amount).toFixed(2)}
                            </Typography>
                        </Box>
                    )}
                    {adminNote && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                            <Typography variant="caption" color="text.secondary">Refund Note:</Typography>
                            <Typography variant="body2">{adminNote}</Typography>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setRefundConfirmOpen(false)} disabled={processingAction}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleRefundTransaction}
                        color="error"
                        variant="contained"
                        disabled={processingAction}
                    >
                        {processingAction ? 'Processing...' : 'Yes, Process Refund'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Retry Payment Dialog */}
            <Dialog
                open={retryPaymentDialogOpen}
                onClose={() => !retryPaymentProcessing && handleCloseRetryPaymentDialog()}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {retryPaymentResults.length > 0 ? 'Retry Results' : 'Retry Failed Payments'}
                </DialogTitle>
                <DialogContent>
                    {!retryPaymentProcessing && retryPaymentResults.length === 0 && (
                        <Box>
                            <Typography variant="body1" gutterBottom>
                                This will attempt to process all failed/pending payments for this member using their current payment method.
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                                <strong>{retryFailedTransactionsCount}</strong> transaction(s) will be retried.
                            </Typography>
                        </Box>
                    )}

                    {retryPaymentProcessing && (
                        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
                            <CircularProgress size={48} />
                            <Typography variant="body1" sx={{ mt: 2 }}>
                                Processing payments...
                            </Typography>
                        </Box>
                    )}

                    {retryPaymentResults.length > 0 && (
                        <Box sx={{ mt: 2 }}>
                            <Typography variant="h6" gutterBottom>
                                Results:
                            </Typography>
                            {retryPaymentResults.map((result, index) => (
                                <Box key={index} sx={{ mb: 2 }}>
                                    <Alert severity={result.success ? 'success' : 'error'}>
                                        <Box>
                                            <Typography variant="body2" fontWeight="bold">
                                                {result.type} - ${Number(result.amount).toFixed(2)}
                                            </Typography>
                                            <Typography variant="body2">
                                                {result.message}
                                            </Typography>
                                        </Box>
                                    </Alert>
                                </Box>
                            ))}
                            <Box sx={{ mt: 2, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                <Typography variant="body2">
                                    <strong>Summary:</strong> {retryPaymentResults.filter(r => r.success).length} succeeded, {retryPaymentResults.filter(r => !r.success).length} failed
                                </Typography>
                            </Box>
                        </Box>
                    )}
                </DialogContent>
                <DialogActions>
                    {retryPaymentResults.length === 0 && !retryPaymentProcessing && (
                        <>
                            <Button onClick={handleCloseRetryPaymentDialog}>
                                Cancel
                            </Button>
                            <Button
                                onClick={handleRetryPayment}
                                color="primary"
                                variant="contained"
                            >
                                Process All Payments Now
                            </Button>
                        </>
                    )}
                    {retryPaymentResults.length > 0 && (
                        <Button onClick={handleCloseRetryPaymentDialog} variant="contained">
                            Close
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Scheduled Downgrade Information Dialog */}
            <Dialog
                open={scheduledDowngradeDialogOpen}
                onClose={() => {
                    setScheduledDowngradeDialogOpen(false);
                    setScheduledDowngradeMessage('');
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Membership Downgrade Scheduled</DialogTitle>
                <DialogContent>
                    <Typography variant="body1" sx={{ mt: 2 }}>
                        {scheduledDowngradeMessage}
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setScheduledDowngradeDialogOpen(false);
                            setScheduledDowngradeMessage('');
                        }}
                        variant="contained"
                        color="primary"
                    >
                        OK
                    </Button>
                </DialogActions>
            </Dialog>

            {/* View/Cancel Scheduled Change Dialog */}
            <Dialog
                open={scheduledChangeDialogOpen}
                onClose={() => {
                    if (!cancelingScheduledChange) {
                        setScheduledChangeDialogOpen(false);
                        setScheduledChangeDetails(null);
                    }
                }}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>Scheduled Membership Change</DialogTitle>
                <DialogContent>
                    {scheduledChangeLoading ? (
                        <Box display="flex" justifyContent="center" p={3}>
                            <CircularProgress />
                        </Box>
                    ) : scheduledChangeDetails ? (
                        <Box sx={{ mt: 2 }}>
                            {scheduledChangeDetails.requestedMembershipTierName === 'Cancelled' ? (
                                <>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>
                                            {scheduledChangeDetails.currentMembershipTierName === 'Current Tier' 
                                                ? memberProfile?.membershipTier?.name || 'Unknown'
                                                : scheduledChangeDetails.currentMembershipTierName}
                                        </strong> membership is scheduled for cancellation
                                    </Typography>
                                    <Typography variant="body1" gutterBottom sx={{ mt: 2 }}>
                                        <strong>Effective Date:</strong>{' '}
                                        {scheduledChangeDetails.effectiveDate
                                            ? formatDate(scheduledChangeDetails.effectiveDate)
                                            : 'Not set'}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Status:</strong> {scheduledChangeDetails.status}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                        The membership will be cancelled on the scheduled date. You can cancel this scheduled cancellation at any time before then.
                                    </Typography>
                                </>
                            ) : (
                                <>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Current Tier:</strong> {scheduledChangeDetails.currentMembershipTierName === 'Current Tier' 
                                            ? memberProfile?.membershipTier?.name || 'Unknown'
                                            : scheduledChangeDetails.currentMembershipTierName}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>New Tier:</strong> {scheduledChangeDetails.requestedMembershipTierName}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Effective Date:</strong>{' '}
                                        {scheduledChangeDetails.effectiveDate
                                            ? formatDate(scheduledChangeDetails.effectiveDate)
                                            : 'Not set'}
                                    </Typography>
                                    <Typography variant="body1" gutterBottom>
                                        <strong>Status:</strong> {scheduledChangeDetails.status}
                                    </Typography>
                                    <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                                        This change will take effect on the scheduled date. You can cancel it at any time before then.
                                    </Typography>
                                </>
                            )}
                        </Box>
                    ) : (
                        <Typography variant="body1" color="text.secondary">
                            No scheduled change details available.
                        </Typography>
                    )}
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            setScheduledChangeDialogOpen(false);
                            setScheduledChangeDetails(null);
                        }}
                        disabled={cancelingScheduledChange}
                    >
                        Close
                    </Button>
                    {scheduledChangeDetails && (
                        <Button
                            onClick={handleCancelScheduledChange}
                            variant="contained"
                            color="error"
                            disabled={cancelingScheduledChange}
                        >
                            {cancelingScheduledChange ? 'Cancelling...' : 'Cancel Change'}
                        </Button>
                    )}
                </DialogActions>
            </Dialog>

            {/* Upload Options Dialog */}
            <Dialog
                open={uploadOptionsOpen}
                onClose={() => setUploadOptionsOpen(false)}
                maxWidth="xs"
                fullWidth
            >
                <DialogTitle>
                    Update Profile Picture
                </DialogTitle>
                <DialogContent>
                    <List sx={{ pt: 0 }}>
                        <ListItemButton onClick={handleUploadFromFile}>
                            <ListItemIcon>
                                <UploadIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Upload from Computer"
                                secondary="Select an image file"
                            />
                        </ListItemButton>
                        <ListItemButton onClick={handleOpenCamera}>
                            <ListItemIcon>
                                <CameraAltIcon />
                            </ListItemIcon>
                            <ListItemText
                                primary="Take a Photo"
                                secondary="Use your camera"
                            />
                        </ListItemButton>
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setUploadOptionsOpen(false)}>
                        Cancel
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Camera Dialog */}
            <Dialog
                open={cameraDialogOpen}
                onClose={handleCloseCameraDialog}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Take a Photo
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ pt: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                        {/* Video Preview */}
                        <Box sx={{
                            width: '100%',
                            maxWidth: 640,
                            backgroundColor: 'black',
                            borderRadius: 2,
                            overflow: 'hidden',
                            position: 'relative'
                        }}>
                            <video
                                ref={videoRef}
                                autoPlay
                                playsInline
                                style={{
                                    width: '100%',
                                    height: 'auto',
                                    display: 'block'
                                }}
                            />
                            {/* Camera overlay guide */}
                            <Box sx={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%)',
                                width: '60%',
                                aspectRatio: '1',
                                border: '3px solid rgba(255, 255, 255, 0.5)',
                                borderRadius: '50%',
                                pointerEvents: 'none'
                            }} />
                        </Box>

                        {/* Hidden canvas for capture */}
                        <canvas ref={canvasRef} style={{ display: 'none' }} />

                        <Typography variant="caption" color="text.secondary" textAlign="center">
                            Position your face within the circle guide
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseCameraDialog}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCapturePhoto}
                        variant="contained"
                        color="primary"
                        startIcon={<PhotoCameraIcon />}
                    >
                        Capture Photo
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Image Crop Dialog */}
            <Dialog
                open={cropDialogOpen}
                onClose={handleCropDialogClose}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    Crop Profile Picture
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
                        {/* Crop Area */}
                        {imageSrc && (
                            <Box sx={{
                                maxHeight: '60vh',
                                overflow: 'auto',
                                display: 'flex',
                                justifyContent: 'center',
                                alignItems: 'center',
                                backgroundColor: 'grey.100',
                                borderRadius: 1,
                                p: 2
                            }}>
                                <ReactCrop
                                    crop={crop}
                                    onChange={(c) => setCrop(c)}
                                    onComplete={(c) => setCompletedCrop(c)}
                                    aspect={1}
                                    circularCrop
                                >
                                    <img
                                        ref={imgRef}
                                        src={imageSrc}
                                        alt="Crop preview"
                                        onLoad={handleImageLoad}
                                        style={{
                                            maxWidth: '100%',
                                            maxHeight: '50vh',
                                            transform: `scale(${zoom})`,
                                            transition: 'transform 0.2s'
                                        }}
                                    />
                                </ReactCrop>
                            </Box>
                        )}

                        {/* Zoom Control */}
                        <Box sx={{ px: 2 }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                <ZoomOutIcon />
                                <Slider
                                    value={zoom}
                                    onChange={(_, value) => setZoom(value as number)}
                                    min={0.5}
                                    max={3}
                                    step={0.1}
                                    valueLabelDisplay="auto"
                                    valueLabelFormat={(value) => `${Math.round(value * 100)}%`}
                                    sx={{ flex: 1 }}
                                />
                                <ZoomInIcon />
                            </Box>
                        </Box>

                        <Typography variant="caption" color="text.secondary" sx={{ px: 2 }}>
                            Drag the crop area to reposition. Use the slider to zoom in/out. On touch devices, you can also pinch to zoom.
                        </Typography>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCropDialogClose} disabled={uploadingPicture}>
                        Cancel
                    </Button>
                    <Button
                        onClick={handleCropComplete}
                        variant="contained"
                        color="primary"
                        disabled={uploadingPicture || !completedCrop}
                    >
                        {uploadingPicture ? <CircularProgress size={24} /> : 'Upload'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Discount Application Dialog */}
            <Dialog
                open={discountApplicationDialogOpen}
                onClose={!discountApplicationLoading ? handleDiscountApplicationCancel : undefined}
                maxWidth="md"
                fullWidth
            >
                <DialogTitle>
                    {pendingDiscountAction?.action === 'add' ? 'Apply Discount to Pending Transactions' : 'Remove Discount from Pending Transactions'}
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body1" gutterBottom>
                        {pendingDiscountAction?.action === 'add'
                            ? `You are adding the discount "${pendingDiscountAction.discount.name}" to this member's profile.`
                            : `You are removing the discount "${pendingDiscountAction?.discount.name}" from this member's profile.`
                        }
                    </Typography>
                    
                    <Typography variant="body1" gutterBottom sx={{ mt: 2, mb: 2 }}>
                        This member has {pendingMembershipTransactions.length} pending membership fee transaction(s):
                    </Typography>

                    <Paper variant="outlined" sx={{ p: 2, mb: 3, maxHeight: '300px', overflowY: 'auto' }}>
                        {pendingMembershipTransactions.map((tx, index) => (
                            <Box
                                key={tx.id}
                                sx={{
                                    py: 1.5,
                                    px: 1,
                                    borderBottom: index < pendingMembershipTransactions.length - 1 ? '1px solid' : 'none',
                                    borderBottomColor: 'divider'
                                }}
                            >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                    <Box>
                                        <Typography variant="body2" fontWeight="medium">
                                            {tx.description || 'Membership Payment'}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">
                                            Transaction Date: {tx.transactionDate ? formatDate(tx.transactionDate) : 'N/A'}
                                        </Typography>
                                    </Box>
                                    <Typography variant="body1" fontWeight="bold">
                                        ${Number(tx.totalAmount).toFixed(2)}
                                    </Typography>
                                </Box>
                                {(Number(tx.discountPercentage) > 0 || Number(tx.discountAmount) > 0) && (
                                    <Typography variant="caption" color="success.main" sx={{ display: 'block', mt: 0.5 }}>
                                        Current discount: {Number(tx.discountPercentage) > 0 ? `${tx.discountPercentage}%` : ''}{Number(tx.discountPercentage) > 0 && Number(tx.discountAmount) > 0 ? ' + ' : ''}${Number(tx.discountAmount) > 0 ? `$${Number(tx.discountAmount).toFixed(2)}` : ''}
                                    </Typography>
                                )}
                            </Box>
                        ))}
                    </Paper>

                    <Alert severity="info" sx={{ mb: 2 }}>
                        <Typography variant="body2" fontWeight="medium" gutterBottom>
                            How would you like to proceed?
                        </Typography>
                        <Typography variant="body2">
                            • <strong>Apply to Existing:</strong> {pendingDiscountAction?.action === 'add' ? 'The discount will be applied to the pending transactions above and their amounts will be recalculated.' : 'The discount will be removed from the pending transactions above and their amounts will be recalculated.'}
                        </Typography>
                        <Typography variant="body2">
                            • <strong>Apply to Future Only:</strong> {pendingDiscountAction?.action === 'add' ? 'The discount will only apply to new transactions created after this change.' : 'The discount will be removed but existing pending transactions will keep their current amounts.'}
                        </Typography>
                        <Typography variant="body2">
                            • <strong>Cancel:</strong> No changes will be made.
                        </Typography>
                    </Alert>

                    <Typography variant="caption" color="text.secondary">
                        Note: Completed or cancelled transactions will not be affected by this change.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <Button
                        onClick={handleDiscountApplicationCancel}
                        disabled={discountApplicationLoading}
                    >
                        Cancel
                    </Button>
                    <Button
                        onClick={handleDiscountApplicationApplyToFuture}
                        disabled={discountApplicationLoading}
                        variant="outlined"
                        startIcon={discountApplicationLoading ? <CircularProgress size={16} /> : undefined}
                    >
                        {discountApplicationLoading ? 'Saving...' : 'Apply to Future Only'}
                    </Button>
                    <Button
                        onClick={handleDiscountApplicationApplyToExisting}
                        disabled={discountApplicationLoading}
                        variant="contained"
                        color="primary"
                        startIcon={discountApplicationLoading ? <CircularProgress size={16} /> : undefined}
                    >
                        {discountApplicationLoading ? 'Applying...' : 'Apply to Existing Transactions'}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Error Dialog */}
            <Dialog open={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Error</DialogTitle>
                <DialogContent>
                    <Typography>{errorDialogMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setErrorDialogOpen(false)} variant="contained" color="primary">
                        OK
                    </Button>
                </DialogActions>
            </Dialog>
        </Container>
    );
};

export default MemberProfileDetailsPage;
